import type { Database } from "@/shared/types/database.types";
import type { LawyerEstateRow } from "@/shared/hooks/useLawyerEstates";
import type { EstateItemsResponse } from "@/shared/types/estateTypes";
import type { InfiniteData } from "@tanstack/react-query";
import type { ContactSourceDocument } from "@/shared/types/estateContact";

type EstateRow = Database["public"]["Tables"]["estates"]["Row"];
type UserRow = Database["public"]["Tables"]["users"]["Row"];
type EstateContactRow = Database["public"]["Tables"]["estate_contacts"]["Row"];
type EstateInvitationRow = Database["public"]["Tables"]["estate_invitations"]["Row"];
type EstateDocumentRow = Database["public"]["Tables"]["estate_documents"]["Row"];

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const DEMO_ESTATE_ID = 999999;
export const DEMO_ESTATE_ID_STR = "999999";
export const DEMO_USER_ID = 9999999;
export const DEMO_OWNER_USER_ID = 1001;

// ---------------------------------------------------------------------------
// Mock user (the demo lawyer)
// ---------------------------------------------------------------------------

export const DEMO_MOCK_USER: UserRow = {
  user_id: DEMO_USER_ID,
  auth_id: "demo-auth-id-000000",
  first_name: "Jack",
  middle_name: null,
  last_name: "Marshall",
  full_name: "Jack Marshall",
  email: "demo@elayne.com",
  phone_number: null,
  phone_number_valid: null,
  date_of_birth: null,
  address: null,
  role: null,
  customizations: { lawyer_viewed_clients: [] },
  completed_onboarding: true,
  completed_authorization: null,
  contact_by_email: null,
  contact_by_phone: null,
  created_at: "2026-01-01T00:00:00.000Z",
  last_active_on: "2026-03-15T09:30:00.000Z",
  last_active_estate_id: null,
  last_page_viewed: null,
  help_topics: null,
  emails_sent: null,
  prepopulation_data: null,
  stripe_customer_id: null,
  tpa_vault_url: null,
  tpa_signature_audit_data: null,
  requested_deletion: null,
  ssn_encrypted: null,
  account_number_encrypted: null,
  routing_number_encrypted: null,
  id_name_match: null,
  revoked_authorization: null,
  failed_login_attempts: 0,
  login_locked_until: null,
};

// ---------------------------------------------------------------------------
// Mock estate (Margaret Whitfield)
// ---------------------------------------------------------------------------

export const DEMO_MOCK_ESTATE: EstateRow = {
  estate_id: DEMO_ESTATE_ID,
  first_name: "Margaret",
  middle_name: null,
  last_name: "Whitfield",
  full_name: "Margaret Whitfield",
  citizenship: null,
  occupation: null,
  employer: null,
  has_trust: null,
  date_of_birth: "1941-10-22",
  date_of_death: "2026-02-17",
  location: "Bedford, NY",
  address: "47 Hawthorne Lane, Bedford, NY 10506",
  place_of_death: "Bedford, NY",
  phone_number: null,
  nickname: null,
  owner_user_id: DEMO_OWNER_USER_ID,
  lawyer_client_status: "letters_approved",
  ssn_encrypted: null,
  ein_encrypted: null,
  passport_number_encrypted: null,
  account_number_encrypted: null,
  routing_number_encrypted: null,
  funeral_home: null,
  funeral_home_reported: false,
  main_image_url: null,
  estate_type: null,
  estate_metadata: null,
  obituary_link: null,
  obituary_data: null,
  marriage_info: null,
  calendly_bookings: null,
  extended_family_heirship: false,
  probate_intake_data: null,
  tax_intake_data: null,
  tracers_response: null,
  family_tree_confirmed: false,
  family_tree_confirmed_at: null,
  family_assertions: null,
  remote_kinship_flag: false,
  created_at: "2026-02-18T10:00:00.000Z",
};

// ---------------------------------------------------------------------------
// Account holder (Robert Whitfield — opened the estate account)
// ---------------------------------------------------------------------------

export const DEMO_MOCK_ACCOUNT_OWNER = {
  full_name: "Robert Whitfield",
  email: "robert.whitfield@example.com",
  phone_number: "(914) 555-0182",
};

// ---------------------------------------------------------------------------
// Lawyer persons (dashboard list)
// ---------------------------------------------------------------------------

export const DEMO_MOCK_LAWYER_PERSONS: LawyerEstateRow[] = [
  {
    id: "demo-lawyer-estate-1",
    estateId: DEMO_ESTATE_ID,
    assignedAt: "2026-03-10T10:00:00.000Z",
    clientName: "Robert Whitfield",
    clientEmail: "robert.whitfield@example.com",
    clientRole: "Executor",
    clientRelationship: "spouse",
    decedentName: "Margaret Whitfield",
    dateOfDeath: "2026-02-17T00:00:00.000Z",
    proceedingType: "Probate",
    authoritySuffix: "Letters issued",
    highlight: {
      category: "action",
      action: "nudge_client",
      label: "Will is missing",
      nextStep: "Nudge client",
      timeAgo: "14d ago",
      timestamp: "2026-03-01T09:30:00.000Z",
    },
    inventoryTotal: 4_895_233,
    assetCount: 17,
    liabilityCount: 1,
    location: "Westchester County, NY",
  },
  {
    id: "demo-lawyer-estate-2",
    estateId: 999998,
    assignedAt: "2026-03-20T10:00:00.000Z",
    clientName: "Diane Ashford-Nguyen",
    clientEmail: "diane.anguyen@example.com",
    clientRole: "Executor",
    clientRelationship: "child",
    decedentName: "Gerald Thomas Ashford",
    dateOfDeath: "2026-03-04T00:00:00.000Z",
    proceedingType: "Probate",
    authoritySuffix: "Pre-letters",
    highlight: {
      category: "passive",
      action: null,
      label: "Estate inventory started",
      nextStep: "See progress",
      timeAgo: "5d ago",
      timestamp: "2026-03-22T10:00:00.000Z",
    },
    inventoryTotal: 1_469_142,
    assetCount: 16,
    liabilityCount: 2,
    location: "Orange County, NC",
  },
  {
    id: "demo-lawyer-estate-3",
    estateId: 999997,
    assignedAt: "2026-04-01T10:00:00.000Z",
    clientName: "Monica Delgado-Reyes",
    clientEmail: "monica.dreyes@example.com",
    clientRole: "Administrator",
    clientRelationship: "child",
    decedentName: "Sylvia Marie Delgado",
    dateOfDeath: "2026-01-22T00:00:00.000Z",
    proceedingType: "Administration",
    authoritySuffix: "Pre-letters",
    highlight: {
      category: "action",
      action: "nudge_client",
      label: "SEP-IRA beneficiary issue",
      nextStep: "Nudge client",
      timeAgo: "8d ago",
      timestamp: "2026-03-24T10:00:00.000Z",
    },
    inventoryTotal: 2_293_558,
    assetCount: 13,
    liabilityCount: 1,
    location: "Travis County, TX",
  },
];

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

type MockDocument = EstateDocumentRow & {
  uploaded_by: Array<{
    user_id: number;
    full_name: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  }>;
};

const uploader = [
  {
    user_id: DEMO_OWNER_USER_ID,
    full_name: "Robert Whitfield",
    first_name: "Robert",
    last_name: "Whitfield",
    email: "robert.whitfield@example.com",
  },
];

export const DEMO_MOCK_DOCUMENTS: MockDocument[] = [
  {
    id: 1001,
    estate_id: DEMO_ESTATE_ID,
    file_name: "Death_Certificate_Margaret_Whitfield.pdf",
    display_name: "Death Certificate",
    document_type: "death_certificate",
    document_key: "demo/documents/death_certificate.pdf",
    extraction_status: "completed",
    file_size: 245000,
    created_at: "2026-02-17T18:30:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID,
    uploaded_by: uploader,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-02-17T18:35:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-02-17T18:31:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 1002,
    estate_id: DEMO_ESTATE_ID,
    file_name: "Last_Will_and_Testament_MEW_2023.pdf",
    display_name: "Last Will and Testament",
    document_type: "will",
    document_key: "demo/documents/will.pdf",
    extraction_status: "completed",
    file_size: 1820000,
    created_at: "2026-02-18T11:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID,
    uploaded_by: uploader,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-02-18T11:10:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-02-18T11:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 1003,
    estate_id: DEMO_ESTATE_ID,
    file_name: "MEW_Revocable_Living_Trust_2021.pdf",
    display_name: "Margaret E. Whitfield Revocable Living Trust",
    document_type: "trust",
    document_key: "demo/documents/trust.pdf",
    extraction_status: "completed",
    file_size: 3240000,
    created_at: "2026-02-18T11:30:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID,
    uploaded_by: uploader,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-02-18T11:45:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-02-18T11:31:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 1004,
    estate_id: DEMO_ESTATE_ID,
    file_name: "Chase_Bank_Statement_Feb2026.pdf",
    display_name: "Chase Bank Statement — February 2026",
    document_type: "bank_statement",
    document_key: "demo/documents/chase_feb.pdf",
    extraction_status: "completed",
    file_size: 412000,
    created_at: "2026-02-24T14:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID,
    uploaded_by: uploader,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-02-24T14:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-02-24T14:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 1005,
    estate_id: DEMO_ESTATE_ID,
    file_name: "Vanguard_IRA_Statement_Q42025.pdf",
    display_name: "Vanguard IRA Statement — Q4 2025",
    document_type: "investment_account_statement",
    document_key: "demo/documents/vanguard_q4.pdf",
    extraction_status: "completed",
    file_size: 680000,
    created_at: "2026-02-28T09:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID,
    uploaded_by: uploader,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-02-28T09:08:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-02-28T09:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 1006,
    estate_id: DEMO_ESTATE_ID,
    file_name: "Fidelity_401k_Statement_Q42025.pdf",
    display_name: "Fidelity 401(k) Statement — Q4 2025",
    document_type: "investment_account_statement",
    document_key: "demo/documents/fidelity_401k.pdf",
    extraction_status: "completed",
    file_size: 524000,
    created_at: "2026-03-01T10:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID,
    uploaded_by: uploader,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-01T10:06:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-01T10:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 1007,
    estate_id: DEMO_ESTATE_ID,
    file_name: "Schwab_Brokerage_Statement_Q42025.pdf",
    display_name: "Charles Schwab Brokerage Statement — Q4 2025",
    document_type: "investment_account_statement",
    document_key: "demo/documents/schwab_q4.pdf",
    extraction_status: "completed",
    file_size: 598000,
    created_at: "2026-03-02T10:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID,
    uploaded_by: uploader,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-02T10:07:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-02T10:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 1008,
    estate_id: DEMO_ESTATE_ID,
    file_name: "Fidelity_Joint_Brokerage_Q42025.pdf",
    display_name: "Fidelity Joint Brokerage Statement — Q4 2025",
    document_type: "investment_account_statement",
    document_key: "demo/documents/fidelity_brokerage.pdf",
    extraction_status: "completed",
    file_size: 611000,
    created_at: "2026-03-03T10:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID,
    uploaded_by: uploader,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-03T10:06:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-03T10:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 1009,
    estate_id: DEMO_ESTATE_ID,
    file_name: "Northwestern_Mutual_Policy_NM7728416.pdf",
    display_name: "Northwestern Mutual Life Insurance Policy",
    document_type: "insurance_document",
    document_key: "demo/documents/nm_insurance.pdf",
    extraction_status: "completed",
    file_size: 1140000,
    created_at: "2026-03-05T14:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID,
    uploaded_by: uploader,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-05T14:12:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-05T14:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 1010,
    estate_id: DEMO_ESTATE_ID,
    file_name: "NY529_Account_Statement_2025.pdf",
    display_name: "NY 529 College Savings Account Statement",
    document_type: "financial_statement",
    document_key: "demo/documents/ny529.pdf",
    extraction_status: "completed",
    file_size: 390000,
    created_at: "2026-03-06T10:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID,
    uploaded_by: uploader,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-06T10:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-06T10:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 1011,
    estate_id: DEMO_ESTATE_ID,
    file_name: "2025_Federal_Tax_Return_1040.pdf",
    display_name: "2025 Federal Income Tax Return (Form 1040)",
    document_type: "tax_document",
    document_key: "demo/documents/1040_2025.pdf",
    extraction_status: "completed",
    file_size: 2100000,
    created_at: "2026-03-10T09:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID,
    uploaded_by: uploader,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-10T09:20:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-10T09:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 1012,
    estate_id: DEMO_ESTATE_ID,
    file_name: "KeyBank_Mortgage_Statement_Feb2026.pdf",
    display_name: "KeyBank Mortgage Statement — February 2026",
    document_type: "mortgage_statement",
    document_key: "demo/documents/keybank_mortgage.pdf",
    extraction_status: "completed",
    file_size: 285000,
    created_at: "2026-03-12T11:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID,
    uploaded_by: uploader,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-12T11:04:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-12T11:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
];

// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------

type MockContact = Omit<EstateContactRow, "is_distributee" | "priority_tier" | "can_serve"> & {
  source_document: ContactSourceDocument | null;
  is_distributee?: boolean;
  priority_tier?: number | null;
  can_serve?: boolean;
};

export const DEMO_MOCK_CONTACTS: MockContact[] = [
  {
    id: 2001,
    estate_id: DEMO_ESTATE_ID,
    estate_owner_id: DEMO_OWNER_USER_ID,
    user_id: DEMO_OWNER_USER_ID,
    first_name: "Robert",
    last_name: "Whitfield",
    middle_name: null,
    nickname: null,
    email: "robert.whitfield@example.com",
    phone: "(914) 555-0182",
    address: "47 Hawthorne Lane, Bedford, NY 10506",
    relationship: "Spouse",
    legal_role: ["executor", "successor_trustee"],
    profile_picture: null,
    invited: true,
    is_hidden: false,
    source_document_id: 1002,
    extracted_from_document: true,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-02-18T11:10:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-02-18T11:10:00.000Z",
    source_document: {
      id: 1002,
      file_name: "Last_Will_and_Testament_MEW_2023.pdf",
      display_name: "Last Will and Testament",
      document_type: "will",
      created_at: "2026-02-18T11:00:00.000Z",
    },
  },
  {
    id: 2002,
    estate_id: DEMO_ESTATE_ID,
    estate_owner_id: DEMO_OWNER_USER_ID,
    user_id: DEMO_OWNER_USER_ID,
    first_name: "Catherine",
    last_name: "Whitfield-Marsh",
    middle_name: null,
    nickname: null,
    email: "catherine.marsh@example.com",
    phone: "(617) 555-0184",
    address: "142 Brattle Street, Cambridge, MA 02138",
    relationship: "Child",
    legal_role: ["beneficiary", "successor_executor"],
    profile_picture: null,
    invited: true,
    is_hidden: false,
    source_document_id: 1002,
    extracted_from_document: true,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-02-18T11:10:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-02-18T11:10:00.000Z",
    source_document: {
      id: 1002,
      file_name: "Last_Will_and_Testament_MEW_2023.pdf",
      display_name: "Last Will and Testament",
      document_type: "will",
      created_at: "2026-02-18T11:00:00.000Z",
    },
  },
  {
    id: 2003,
    estate_id: DEMO_ESTATE_ID,
    estate_owner_id: DEMO_OWNER_USER_ID,
    user_id: DEMO_OWNER_USER_ID,
    first_name: "Andrew",
    last_name: "Whitfield",
    middle_name: null,
    nickname: null,
    email: "andrew@whitfieldarchitects.example",
    phone: "(518) 555-0247",
    address: "87 Caroline Street, Saratoga Springs, NY 12866",
    relationship: "Child",
    legal_role: ["beneficiary"],
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: 1002,
    extracted_from_document: true,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-02-18T11:10:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-02-18T11:10:00.000Z",
    source_document: {
      id: 1002,
      file_name: "Last_Will_and_Testament_MEW_2023.pdf",
      display_name: "Last Will and Testament",
      document_type: "will",
      created_at: "2026-02-18T11:00:00.000Z",
    },
  },
  {
    id: 2004,
    estate_id: DEMO_ESTATE_ID,
    estate_owner_id: DEMO_OWNER_USER_ID,
    user_id: DEMO_OWNER_USER_ID,
    first_name: "Rebecca",
    last_name: "Whitfield-Chen",
    middle_name: null,
    nickname: null,
    email: "rebecca.wchen@example.com",
    phone: "(503) 555-0392",
    address: "2418 NW Thurman Street, Portland, OR 97210",
    relationship: "Child",
    legal_role: ["beneficiary"],
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: 1002,
    extracted_from_document: true,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-02-18T11:10:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-02-18T11:10:00.000Z",
    source_document: {
      id: 1002,
      file_name: "Last_Will_and_Testament_MEW_2023.pdf",
      display_name: "Last Will and Testament",
      document_type: "will",
      created_at: "2026-02-18T11:00:00.000Z",
    },
  },
  {
    id: 2005,
    estate_id: DEMO_ESTATE_ID,
    estate_owner_id: DEMO_OWNER_USER_ID,
    user_id: DEMO_OWNER_USER_ID,
    first_name: "Thomas",
    last_name: "Marsh",
    middle_name: null,
    nickname: null,
    email: null,
    phone: null,
    address: "142 Brattle Street, Cambridge, MA 02138",
    relationship: "Son-in-law",
    legal_role: null,
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-03-12T11:00:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-03-12T11:00:00.000Z",
    source_document: null,
  },
  {
    id: 2006,
    estate_id: DEMO_ESTATE_ID,
    estate_owner_id: DEMO_OWNER_USER_ID,
    user_id: DEMO_OWNER_USER_ID,
    first_name: "Eleanor",
    last_name: "Marsh",
    middle_name: null,
    nickname: null,
    email: "ellie.marsh@example.com",
    phone: "(617) 555-0418",
    address: "318 W 105th Street, Apt 4B, New York, NY 10025",
    relationship: "Grandchild",
    legal_role: ["beneficiary"],
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-03-12T11:30:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-03-12T11:30:00.000Z",
    source_document: null,
  },
  {
    id: 2007,
    estate_id: DEMO_ESTATE_ID,
    estate_owner_id: DEMO_OWNER_USER_ID,
    user_id: DEMO_OWNER_USER_ID,
    first_name: "Oliver",
    last_name: "Marsh",
    middle_name: null,
    nickname: null,
    email: null,
    phone: null,
    address: "142 Brattle Street, Cambridge, MA 02138",
    relationship: "Grandchild",
    legal_role: ["beneficiary"],
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-03-12T11:30:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-03-12T11:30:00.000Z",
    source_document: null,
  },
  {
    id: 2008,
    estate_id: DEMO_ESTATE_ID,
    estate_owner_id: DEMO_OWNER_USER_ID,
    user_id: DEMO_OWNER_USER_ID,
    first_name: "Lily",
    last_name: "Whitfield",
    middle_name: null,
    nickname: null,
    email: null,
    phone: null,
    address: "87 Caroline Street, Saratoga Springs, NY 12866",
    relationship: "Grandchild",
    legal_role: ["beneficiary"],
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-03-12T11:30:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-03-12T11:30:00.000Z",
    source_document: null,
  },
  {
    id: 2009,
    estate_id: DEMO_ESTATE_ID,
    estate_owner_id: DEMO_OWNER_USER_ID,
    user_id: DEMO_OWNER_USER_ID,
    first_name: "James",
    last_name: "Whitfield",
    middle_name: null,
    nickname: null,
    email: null,
    phone: null,
    address: "87 Caroline Street, Saratoga Springs, NY 12866",
    relationship: "Grandchild",
    legal_role: ["beneficiary"],
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-03-12T11:30:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-03-12T11:30:00.000Z",
    source_document: null,
  },
  {
    id: 2010,
    estate_id: DEMO_ESTATE_ID,
    estate_owner_id: DEMO_OWNER_USER_ID,
    user_id: DEMO_OWNER_USER_ID,
    first_name: "Maya",
    last_name: "Chen",
    middle_name: null,
    nickname: null,
    email: null,
    phone: null,
    address: "2418 NW Thurman Street, Portland, OR 97210",
    relationship: "Grandchild",
    legal_role: ["beneficiary"],
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-03-12T11:30:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-03-12T11:30:00.000Z",
    source_document: null,
  },
  {
    id: 2011,
    estate_id: DEMO_ESTATE_ID,
    estate_owner_id: DEMO_OWNER_USER_ID,
    user_id: DEMO_OWNER_USER_ID,
    first_name: "Ethan",
    last_name: "Chen",
    middle_name: null,
    nickname: null,
    email: null,
    phone: null,
    address: "2418 NW Thurman Street, Portland, OR 97210",
    relationship: "Grandchild",
    legal_role: ["beneficiary"],
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-03-12T11:30:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-03-12T11:30:00.000Z",
    source_document: null,
  },
];

// ---------------------------------------------------------------------------
// Invitations
// ---------------------------------------------------------------------------

export const DEMO_MOCK_INVITATIONS: EstateInvitationRow[] = [
  {
    id: 3001,
    estate_id: DEMO_ESTATE_ID,
    email: "robert.whitfield@example.com",
    role: "executor",
    status: "accepted",
    invitation_token: "demo-token-robert",
    invited_by_user_id: DEMO_USER_ID,
    is_attorney: false,
    created_at: "2026-02-18T10:30:00.000Z",
    updated_at: "2026-02-19T08:00:00.000Z",
    expires_at: "2026-03-18T10:30:00.000Z",
  },
  {
    id: 3002,
    estate_id: DEMO_ESTATE_ID,
    email: "catherine.marsh@example.com",
    role: "beneficiary",
    status: "pending",
    invitation_token: "demo-token-catherine",
    invited_by_user_id: DEMO_USER_ID,
    is_attorney: false,
    created_at: "2026-02-20T15:00:00.000Z",
    updated_at: "2026-02-20T15:00:00.000Z",
    expires_at: "2026-03-20T15:00:00.000Z",
  },
  {
    id: 3003,
    estate_id: DEMO_ESTATE_ID,
    email: "andrew@whitfieldarchitects.example",
    role: "beneficiary",
    status: "pending",
    invitation_token: "demo-token-andrew",
    invited_by_user_id: DEMO_USER_ID,
    is_attorney: false,
    created_at: "2026-03-12T12:00:00.000Z",
    updated_at: "2026-03-12T12:00:00.000Z",
    expires_at: "2026-04-12T12:00:00.000Z",
  },
];

// ---------------------------------------------------------------------------
// Joined users
// ---------------------------------------------------------------------------

export const DEMO_MOCK_JOINED_USERS = [
  { user_id: DEMO_OWNER_USER_ID, email: "robert.whitfield@example.com" },
];

// ---------------------------------------------------------------------------
// Estate items
// ---------------------------------------------------------------------------

import type { EstateItem, SourceDocument } from "@/shared/types/estateTypes";

const now = "2026-03-15T09:30:00.000Z";

const mkDoc = (
  id: number,
  file_name: string,
  display_name: string,
  document_type: string,
  created_at: string,
): SourceDocument => ({ id, file_name, display_name, document_type, created_at });

const srcChase = mkDoc(
  1004,
  "Chase_Bank_Statement_Feb2026.pdf",
  "Chase Bank Statement — February 2026",
  "bank_statement",
  "2026-02-24T14:00:00.000Z",
);
const srcVanguardIRA = mkDoc(
  1005,
  "Vanguard_IRA_Statement_Q42025.pdf",
  "Vanguard IRA Statement — Q4 2025",
  "investment_account_statement",
  "2026-02-28T09:00:00.000Z",
);
const srcFidelity401k = mkDoc(
  1006,
  "Fidelity_401k_Statement_Q42025.pdf",
  "Fidelity 401(k) Statement — Q4 2025",
  "investment_account_statement",
  "2026-03-01T10:00:00.000Z",
);
const srcSchwab = mkDoc(
  1007,
  "Schwab_Brokerage_Statement_Q42025.pdf",
  "Charles Schwab Brokerage Statement — Q4 2025",
  "investment_account_statement",
  "2026-03-02T10:00:00.000Z",
);
const srcFidelityBrokerage = mkDoc(
  1008,
  "Fidelity_Joint_Brokerage_Q42025.pdf",
  "Fidelity Joint Brokerage Statement — Q4 2025",
  "investment_account_statement",
  "2026-03-03T10:00:00.000Z",
);
const srcNM = mkDoc(
  1009,
  "Northwestern_Mutual_Policy_NM7728416.pdf",
  "Northwestern Mutual Life Insurance Policy",
  "insurance_document",
  "2026-03-05T14:00:00.000Z",
);
const srcNY529 = mkDoc(
  1010,
  "NY529_Account_Statement_2025.pdf",
  "NY 529 College Savings Account Statement",
  "financial_statement",
  "2026-03-06T10:00:00.000Z",
);
const srcMortgage = mkDoc(
  1012,
  "KeyBank_Mortgage_Statement_Feb2026.pdf",
  "KeyBank Mortgage Statement — February 2026",
  "mortgage_statement",
  "2026-03-12T11:00:00.000Z",
);

const baseItem = (
  fields: Partial<EstateItem> &
    Pick<EstateItem, "id" | "category" | "item_name" | "balance" | "classification">,
): EstateItem => ({
  estate_id: DEMO_ESTATE_ID,
  item_type: undefined,
  ownership: null,
  created_at: now,
  updated_at: now,
  created_by_user_id: DEMO_OWNER_USER_ID,
  last_updated_date: now,
  notes: undefined,
  plaid_connection_id: null,
  plaid_account_id: null,
  plaid_account_mask: null,
  plaid_last_refreshed_at: null,
  plaid_institution_logo: null,
  source_document_id: null,
  extracted_from_document: false,
  extraction_status: "approved",
  open_date: null,
  identifiers: {},
  metadata: {},
  is_estimated: false,
  balance_estimate_range: null,
  value_label: null,
  status_flag: null,
  linked_item_id: null,
  source_document: null,
  document_item_references: null,
  ...fields,
});

const mockAssets: EstateItem[] = [
  baseItem({
    id: 4001,
    category: "Properties",
    item_name: "Bedford Residence — 47 Hawthorne Lane",
    item_type: "Residential",
    balance: 2_400_000,
    classification: "asset",
    ownership: "jtwros",
    identifiers: { address: "47 Hawthorne Lane, Bedford, NY 10506" },
    linked_item_id: 4101,
  }),
  baseItem({
    id: 4002,
    category: "Properties",
    item_name: "Skaneateles Vacation Home — 84 Lake Road",
    item_type: "Secondary/vacation homes",
    balance: 385_000,
    classification: "asset",
    ownership: "revocable_trust",
    identifiers: { address: "84 Lake Road, Skaneateles, NY 13152" },
  }),
  baseItem({
    id: 4003,
    category: "Bank accounts",
    item_name: "Chase Joint Checking (–4108)",
    item_type: "Checking accounts",
    balance: 34_820.16,
    classification: "asset",
    ownership: "jtwros",
    identifiers: { institution: "Chase", account_type: "checking", account_last4: "4108" },
    source_document_id: 1004,
    extracted_from_document: true,
    source_document: srcChase,
  }),
  baseItem({
    id: 4004,
    category: "Bank accounts",
    item_name: "Chase Savings (–6271)",
    item_type: "Savings accounts",
    balance: 87_540.22,
    classification: "asset",
    ownership: "revocable_trust",
    identifiers: { institution: "Chase", account_type: "savings", account_last4: "6271" },
    source_document_id: 1004,
    extracted_from_document: true,
    source_document: srcChase,
  }),
  baseItem({
    id: 4005,
    category: "Bank accounts",
    item_name: "Marcus by Goldman Sachs HYSA (–3592)",
    item_type: "Savings accounts",
    balance: 145_300,
    classification: "asset",
    ownership: "jtwros",
    identifiers: {
      institution: "Marcus by Goldman Sachs",
      account_type: "savings",
      account_last4: "3592",
    },
  }),
  baseItem({
    id: 4006,
    category: "Bank accounts",
    item_name: "M&T Bank Municipal Bond CD (–9912)",
    item_type: "Certificates of Deposit (CDs)",
    balance: 52_180,
    classification: "asset",
    ownership: "revocable_trust",
    identifiers: { institution: "M&T Bank", account_type: "cd", account_last4: "9912" },
  }),
  baseItem({
    id: 4007,
    category: "Retirement accounts",
    item_name: "Vanguard Traditional IRA (–7821)",
    item_type: "Traditional IRA",
    balance: 612_485,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: { institution: "Vanguard", account_type: "ira", account_last4: "7821" },
    source_document_id: 1005,
    extracted_from_document: true,
    source_document: srcVanguardIRA,
  }),
  baseItem({
    id: 4008,
    category: "Retirement accounts",
    item_name: "Vanguard Roth IRA (–7822)",
    item_type: "Roth IRA",
    balance: 94_650,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: { institution: "Vanguard", account_type: "roth_ira", account_last4: "7822" },
    source_document_id: 1005,
    extracted_from_document: true,
    extraction_status: "pending",
    source_document: srcVanguardIRA,
  }),
  baseItem({
    id: 4009,
    category: "Retirement accounts",
    item_name: "Fidelity 401(k) (–3304)",
    item_type: "401(k)",
    balance: 185_200,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: { institution: "Fidelity", account_type: "401k", account_last4: "3304" },
    source_document_id: 1006,
    extracted_from_document: true,
    source_document: srcFidelity401k,
  }),
  baseItem({
    id: 4010,
    category: "Brokerage accounts",
    item_name: "Fidelity Joint Brokerage (–5520)",
    item_type: "Individual brokerage account",
    balance: 285_400,
    classification: "asset",
    ownership: "jtwros",
    identifiers: { institution: "Fidelity", account_type: "brokerage", account_last4: "5520" },
    source_document_id: 1008,
    extracted_from_document: true,
    source_document: srcFidelityBrokerage,
  }),
  baseItem({
    id: 4011,
    category: "Brokerage accounts",
    item_name: "Charles Schwab Individual Brokerage (–8847)",
    item_type: "Individual brokerage account",
    balance: 178_900,
    classification: "asset",
    ownership: "revocable_trust",
    identifiers: {
      institution: "Charles Schwab",
      account_type: "brokerage",
      account_last4: "8847",
    },
    source_document_id: 1007,
    extracted_from_document: true,
    extraction_status: "pending",
    source_document: srcSchwab,
  }),
  baseItem({
    id: 4012,
    category: "Tax-advantaged savings",
    item_name: "NY 529 College Savings Plans — 6 Beneficiaries",
    item_type: "529 Education Account",
    balance: 270_000,
    classification: "asset",
    ownership: "pod_tod",
    identifiers: { institution: "New York 529" },
    source_document_id: 1010,
    extracted_from_document: true,
    source_document: srcNY529,
  }),
  baseItem({
    id: 4013,
    category: "Life insurance",
    item_name: "Northwestern Mutual Term Life (NM-77-2841-6)",
    item_type: "Term life insurance",
    balance: 250_000,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: {
      institution: "Northwestern Mutual",
      provider: "Northwestern Mutual Life Insurance Company",
      policy_type: "term",
      policy_number: "NM-77-2841-6",
      account_last4: "2841",
    },
    source_document_id: 1009,
    extracted_from_document: true,
    source_document: srcNM,
  }),
  baseItem({
    id: 4014,
    category: "Vehicles",
    item_name: "2022 Lexus ES 350",
    item_type: "Cars and motorcycles",
    balance: 34_500,
    classification: "asset",
    ownership: "jtwros",
    identifiers: { make: "Lexus", model: "ES 350", year: "2022" },
  }),
  baseItem({
    id: 4015,
    category: "Valuables",
    item_name: "Household Furnishings & Art Collection",
    item_type: "Art and collectibles",
    balance: 95_000,
    classification: "asset",
    ownership: null,
    is_estimated: true,
  }),
  baseItem({
    id: 4016,
    category: "Valuables",
    item_name: "Jewelry (inherited from mother)",
    item_type: "Jewelry & watches",
    balance: 42_000,
    classification: "asset",
    ownership: null,
    is_estimated: true,
  }),
  baseItem({
    id: 4017,
    category: "Valuables",
    item_name: "Other Personal Effects",
    item_type: "Other valuable personal property",
    balance: 11_000,
    classification: "asset",
    ownership: null,
  }),
];

const mockDebts: EstateItem[] = [
  baseItem({
    id: 4101,
    category: "Secured debts",
    item_name: "KeyBank Mortgage — 47 Hawthorne Lane",
    item_type: "Mortgages",
    balance: 268_742.16,
    classification: "debt",
    ownership: null,
    identifiers: { institution: "KeyBank", account_last4: "0604", loan_type: "mortgage" },
    source_document_id: 1012,
    extracted_from_document: true,
    source_document: srcMortgage,
    linked_item_id: 4001,
  }),
];

const totalAssets = mockAssets.filter((a) => !a.value_label).reduce((sum, a) => sum + a.balance, 0);
const totalDebts = mockDebts.filter((d) => !d.value_label).reduce((sum, d) => sum + d.balance, 0);

const CATEGORY_COLORS: Record<
  string,
  { colorClass: string; bgOpacityClass: string; textColorClass: string }
> = {
  Properties: {
    colorClass: "bg-purple-500",
    bgOpacityClass: "bg-purple-500/10",
    textColorClass: "text-purple-500",
  },
  "Life insurance": {
    colorClass: "bg-teal-400",
    bgOpacityClass: "bg-teal-400/10",
    textColorClass: "text-teal-400",
  },
  Vehicles: {
    colorClass: "bg-green-400",
    bgOpacityClass: "bg-green-400/10",
    textColorClass: "text-green-400",
  },
  "Brokerage accounts": {
    colorClass: "bg-orange-300",
    bgOpacityClass: "bg-orange-300/10",
    textColorClass: "text-orange-300",
  },
  "Tax-advantaged savings": {
    colorClass: "bg-lime-500",
    bgOpacityClass: "bg-lime-500/10",
    textColorClass: "text-lime-500",
  },
  "Retirement accounts": {
    colorClass: "bg-emerald-600",
    bgOpacityClass: "bg-emerald-600/10",
    textColorClass: "text-emerald-600",
  },
  "Bank accounts": {
    colorClass: "bg-red-400",
    bgOpacityClass: "bg-red-400/10",
    textColorClass: "text-red-400",
  },
  Valuables: {
    colorClass: "bg-yellow-500",
    bgOpacityClass: "bg-yellow-500/10",
    textColorClass: "text-yellow-500",
  },
  "Secured debts": {
    colorClass: "bg-blue-500",
    bgOpacityClass: "bg-blue-500/10",
    textColorClass: "text-blue-500",
  },
};

const categoryMap: Record<string, { value: number; accountCount: number }> = {};
mockAssets
  .filter((a) => !a.value_label)
  .forEach((item) => {
    if (!categoryMap[item.category]) categoryMap[item.category] = { value: 0, accountCount: 0 };
    categoryMap[item.category].value += item.balance;
    categoryMap[item.category].accountCount += 1;
  });

const mockCategories = Object.entries(categoryMap).map(([category, data]) => ({
  category,
  value: data.value,
  accountCount: data.accountCount,
  percentage: totalAssets > 0 ? (data.value / totalAssets) * 100 : 0,
  icon: "",
  ...(CATEGORY_COLORS[category] ?? {
    colorClass: "bg-gray-400",
    bgOpacityClass: "bg-gray-400/10",
    textColorClass: "text-gray-400",
  }),
}));

export const DEMO_MOCK_ESTATE_ITEMS: EstateItemsResponse = {
  assets: mockAssets,
  debts: mockDebts,
  summary: {
    totalAssets,
    totalDebts,
    netValue: totalAssets - totalDebts,
    hasItems: true,
    categories: mockCategories,
    pendingValuationCount: 0,
  },
};

// ---------------------------------------------------------------------------
// Per-item beneficiaries
// ---------------------------------------------------------------------------

import type { ItemBeneficiary } from "@/shared/hooks/useItemBeneficiaries";
import type { DocumentReference } from "@/shared/hooks/useItemDocumentReferences";

const mkBeneficiary = (
  id: number,
  contact_id: number,
  contact_name: string,
  relationship: string,
  share_percentage: number | null,
  numerator: number | null,
  denominator: number | null,
  is_contingent: boolean,
): ItemBeneficiary => ({
  id,
  contact_id,
  contact_name,
  relationship,
  share_percentage,
  numerator,
  denominator,
  is_contingent,
});

export const DEMO_MOCK_ITEM_BENEFICIARIES: Record<number, ItemBeneficiary[]> = {
  // Vanguard Traditional IRA — Robert 100% primary
  4007: [mkBeneficiary(6001, 2001, "Robert Whitfield", "Spouse", 100, null, null, false)],
  // Vanguard Roth IRA — children equal split primary
  4008: [
    mkBeneficiary(6002, 2002, "Catherine Whitfield-Marsh", "Child", null, 1, 3, false),
    mkBeneficiary(6003, 2003, "Andrew Whitfield", "Child", null, 1, 3, false),
    mkBeneficiary(6004, 2004, "Rebecca Whitfield-Chen", "Child", null, 1, 3, false),
  ],
  // Fidelity 401(k) — Robert 100% primary
  4009: [mkBeneficiary(6005, 2001, "Robert Whitfield", "Spouse", 100, null, null, false)],
  // NW Mutual Life Insurance — Robert 100% primary
  4013: [mkBeneficiary(6006, 2001, "Robert Whitfield", "Spouse", 100, null, null, false)],
  // NY 529 — all 6 grandchildren, equal 1/6 share each
  4012: [
    mkBeneficiary(6010, 2006, "Eleanor Marsh", "Grandchild", null, 1, 6, false),
    mkBeneficiary(6011, 2007, "Oliver Marsh", "Grandchild", null, 1, 6, false),
    mkBeneficiary(6012, 2008, "Lily Whitfield", "Grandchild", null, 1, 6, false),
    mkBeneficiary(6013, 2009, "James Whitfield", "Grandchild", null, 1, 6, false),
    mkBeneficiary(6014, 2010, "Maya Chen", "Grandchild", null, 1, 6, false),
    mkBeneficiary(6015, 2011, "Ethan Chen", "Grandchild", null, 1, 6, false),
  ],
};

// ---------------------------------------------------------------------------
// Per-item document references
// ---------------------------------------------------------------------------

const mkDocRef = (
  id: number,
  document_id: number,
  estate_item_id: number,
  review_outcome: string,
  file_name: string,
  display_name: string,
  document_type: string,
  document_created_at: string,
  created_at: string,
): DocumentReference => ({
  id,
  document_id,
  estate_item_id,
  created_at,
  review_outcome,
  file_name,
  display_name,
  document_type,
  document_created_at,
});

export const DEMO_MOCK_ITEM_DOCUMENT_REFS: Record<number, DocumentReference[]> = {
  4001: [
    mkDocRef(
      7011,
      1012,
      4001,
      "approved",
      "KeyBank_Mortgage_Statement_Feb2026.pdf",
      "KeyBank Mortgage Statement — February 2026",
      "mortgage_statement",
      "2026-03-12T11:00:00.000Z",
      "2026-03-12T12:00:00.000Z",
    ),
    mkDocRef(
      7012,
      1003,
      4001,
      "approved",
      "MEW_Revocable_Living_Trust_2021.pdf",
      "Margaret E. Whitfield Revocable Living Trust",
      "trust",
      "2026-02-18T11:30:00.000Z",
      "2026-02-18T12:00:00.000Z",
    ),
  ],
  4002: [
    mkDocRef(
      7013,
      1003,
      4002,
      "approved",
      "MEW_Revocable_Living_Trust_2021.pdf",
      "Margaret E. Whitfield Revocable Living Trust",
      "trust",
      "2026-02-18T11:30:00.000Z",
      "2026-02-18T12:00:00.000Z",
    ),
  ],
  4003: [
    mkDocRef(
      7001,
      1004,
      4003,
      "approved",
      "Chase_Bank_Statement_Feb2026.pdf",
      "Chase Bank Statement — February 2026",
      "bank_statement",
      "2026-02-24T14:00:00.000Z",
      "2026-02-25T09:00:00.000Z",
    ),
  ],
  4004: [
    mkDocRef(
      7002,
      1004,
      4004,
      "approved",
      "Chase_Bank_Statement_Feb2026.pdf",
      "Chase Bank Statement — February 2026",
      "bank_statement",
      "2026-02-24T14:00:00.000Z",
      "2026-02-25T09:00:00.000Z",
    ),
  ],
  4007: [
    mkDocRef(
      7003,
      1005,
      4007,
      "approved",
      "Vanguard_IRA_Statement_Q42025.pdf",
      "Vanguard IRA Statement — Q4 2025",
      "investment_account_statement",
      "2026-02-28T09:00:00.000Z",
      "2026-02-28T10:00:00.000Z",
    ),
  ],
  4008: [
    mkDocRef(
      7004,
      1005,
      4008,
      "pending",
      "Vanguard_IRA_Statement_Q42025.pdf",
      "Vanguard IRA Statement — Q4 2025",
      "investment_account_statement",
      "2026-02-28T09:00:00.000Z",
      "2026-02-28T10:00:00.000Z",
    ),
  ],
  4009: [
    mkDocRef(
      7005,
      1006,
      4009,
      "approved",
      "Fidelity_401k_Statement_Q42025.pdf",
      "Fidelity 401(k) Statement — Q4 2025",
      "investment_account_statement",
      "2026-03-01T10:00:00.000Z",
      "2026-03-01T11:00:00.000Z",
    ),
  ],
  4010: [
    mkDocRef(
      7006,
      1008,
      4010,
      "approved",
      "Fidelity_Joint_Brokerage_Q42025.pdf",
      "Fidelity Joint Brokerage Statement — Q4 2025",
      "investment_account_statement",
      "2026-03-03T10:00:00.000Z",
      "2026-03-03T11:00:00.000Z",
    ),
  ],
  4011: [
    mkDocRef(
      7007,
      1007,
      4011,
      "pending",
      "Schwab_Brokerage_Statement_Q42025.pdf",
      "Charles Schwab Brokerage Statement — Q4 2025",
      "investment_account_statement",
      "2026-03-02T10:00:00.000Z",
      "2026-03-02T11:00:00.000Z",
    ),
  ],
  4012: [
    mkDocRef(
      7008,
      1010,
      4012,
      "approved",
      "NY529_Account_Statement_2025.pdf",
      "NY 529 College Savings Account Statement",
      "financial_statement",
      "2026-03-06T10:00:00.000Z",
      "2026-03-06T11:00:00.000Z",
    ),
  ],
  4013: [
    mkDocRef(
      7009,
      1009,
      4013,
      "approved",
      "Northwestern_Mutual_Policy_NM7728416.pdf",
      "Northwestern Mutual Life Insurance Policy",
      "insurance_document",
      "2026-03-05T14:00:00.000Z",
      "2026-03-05T15:00:00.000Z",
    ),
  ],
  4101: [
    mkDocRef(
      7010,
      1012,
      4101,
      "approved",
      "KeyBank_Mortgage_Statement_Feb2026.pdf",
      "KeyBank Mortgage Statement — February 2026",
      "mortgage_statement",
      "2026-03-12T11:00:00.000Z",
      "2026-03-12T12:00:00.000Z",
    ),
  ],
};

// ---------------------------------------------------------------------------
// Activity log
// ---------------------------------------------------------------------------

import type { ActivityLogEntry, ActionType, ActorType } from "@/shared/types/activityLog";

interface ActivityLogPage {
  entries: ActivityLogEntry[];
  nextCursor: { created_at: string; id: number } | null;
}

const logEntry = (
  id: number,
  created_at: string,
  action_type: ActionType,
  actor_type: ActorType,
  actor_name: string,
  actor_email: string,
  entity_type: string | null = null,
  metadata: Record<string, unknown> = {},
): ActivityLogEntry => ({
  id,
  created_at,
  estate_id: DEMO_ESTATE_ID,
  action_type,
  actor_type,
  user_id: actor_type === "lawyer" ? DEMO_USER_ID : DEMO_OWNER_USER_ID,
  admin_id: null,
  entity_type,
  entity_id: null,
  metadata,
  actor_name,
  actor_email,
});

const mockActivityEntries: ActivityLogEntry[] = [
  logEntry(
    5015,
    "2026-03-15T09:30:00.000Z",
    "updated_deceased_profile",
    "user",
    "Robert Whitfield",
    "robert.whitfield@example.com",
    "estate",
  ),
  logEntry(
    5014,
    "2026-03-12T11:00:00.000Z",
    "added_contact",
    "user",
    "Robert Whitfield",
    "robert.whitfield@example.com",
    "contact",
    { name: "Andrew Whitfield", relationship: "Child" },
  ),
  logEntry(
    5013,
    "2026-03-12T10:00:00.000Z",
    "uploaded_document",
    "user",
    "Robert Whitfield",
    "robert.whitfield@example.com",
    "document",
    { document_type: "mortgage_statement", file_name: "KeyBank_Mortgage_Statement_Feb2026.pdf" },
  ),
  logEntry(
    5012,
    "2026-03-10T09:00:00.000Z",
    "uploaded_document",
    "user",
    "Robert Whitfield",
    "robert.whitfield@example.com",
    "document",
    { document_type: "tax_document", file_name: "2025_Federal_Tax_Return_1040.pdf" },
  ),
  logEntry(
    5011,
    "2026-03-05T14:00:00.000Z",
    "added_estate_item",
    "user",
    "Robert Whitfield",
    "robert.whitfield@example.com",
    "estate_item",
    { item_name: "KeyBank Mortgage", category: "Secured debts", balance: 268742.16 },
  ),
  logEntry(
    5010,
    "2026-03-03T10:00:00.000Z",
    "updated_estate_item",
    "lawyer",
    "Elayne Legal",
    "demo@elayne.com",
    "estate_item",
    { item_name: "Bedford Residence", category: "Properties", balance: 2400000 },
  ),
  logEntry(
    5009,
    "2026-03-01T10:00:00.000Z",
    "uploaded_document",
    "user",
    "Robert Whitfield",
    "robert.whitfield@example.com",
    "document",
    {
      document_type: "investment_account_statement",
      file_name: "Fidelity_401k_Statement_Q42025.pdf",
    },
  ),
  logEntry(
    5008,
    "2026-02-28T09:00:00.000Z",
    "uploaded_document",
    "user",
    "Robert Whitfield",
    "robert.whitfield@example.com",
    "document",
    {
      document_type: "investment_account_statement",
      file_name: "Vanguard_IRA_Statement_Q42025.pdf",
    },
  ),
  logEntry(
    5007,
    "2026-02-25T11:00:00.000Z",
    "added_estate_item",
    "user",
    "Robert Whitfield",
    "robert.whitfield@example.com",
    "estate_item",
    { item_name: "Vanguard Traditional IRA", category: "Retirement accounts", balance: 612485 },
  ),
  logEntry(
    5006,
    "2026-02-24T14:00:00.000Z",
    "uploaded_document",
    "user",
    "Robert Whitfield",
    "robert.whitfield@example.com",
    "document",
    { document_type: "bank_statement", file_name: "Chase_Bank_Statement_Feb2026.pdf" },
  ),
  logEntry(
    5005,
    "2026-02-22T10:00:00.000Z",
    "added_estate_item",
    "user",
    "Robert Whitfield",
    "robert.whitfield@example.com",
    "estate_item",
    { item_name: "Bedford Residence", category: "Properties", balance: 2400000 },
  ),
  logEntry(
    5004,
    "2026-02-20T16:00:00.000Z",
    "invited_contact",
    "lawyer",
    "Elayne Legal",
    "demo@elayne.com",
    "contact",
    { email: "catherine.marsh@example.com" },
  ),
  logEntry(
    5003,
    "2026-02-20T14:00:00.000Z",
    "added_contact",
    "user",
    "Robert Whitfield",
    "robert.whitfield@example.com",
    "contact",
    { name: "Catherine Whitfield-Marsh", relationship: "Child" },
  ),
  logEntry(
    5002,
    "2026-02-18T11:00:00.000Z",
    "uploaded_document",
    "user",
    "Robert Whitfield",
    "robert.whitfield@example.com",
    "document",
    { document_type: "will", file_name: "Last_Will_and_Testament_MEW_2023.pdf" },
  ),
  logEntry(
    5001,
    "2026-02-17T18:30:00.000Z",
    "uploaded_document",
    "user",
    "Robert Whitfield",
    "robert.whitfield@example.com",
    "document",
    { document_type: "death_certificate", file_name: "Death_Certificate_Margaret_Whitfield.pdf" },
  ),
];

export const DEMO_MOCK_ACTIVITY_LOG: InfiniteData<ActivityLogPage> = {
  pages: [{ entries: mockActivityEntries, nextCursor: null }],
  pageParams: [null],
};

// ---------------------------------------------------------------------------
// Per-item activity logs
// ---------------------------------------------------------------------------

const itemLogEntry = (
  id: number,
  created_at: string,
  action_type: ActionType,
  actor_type: ActorType,
  actor_name: string,
  actor_email: string,
  itemId: number,
  metadata: Record<string, unknown> = {},
): ActivityLogEntry => ({
  id,
  created_at,
  estate_id: DEMO_ESTATE_ID,
  action_type,
  actor_type,
  user_id: actor_type === "lawyer" ? DEMO_USER_ID : DEMO_OWNER_USER_ID,
  admin_id: null,
  entity_type: "estate_item",
  entity_id: String(itemId),
  metadata,
  actor_name,
  actor_email,
});

const mkItemLog = (entries: ActivityLogEntry[]): InfiniteData<ActivityLogPage> => ({
  pages: [{ entries, nextCursor: null }],
  pageParams: [null],
});

export const DEMO_MOCK_ITEM_ACTIVITY_LOGS: Record<number, InfiniteData<ActivityLogPage>> = {
  // Bedford Residence — manually added, then value updated
  4001: mkItemLog([
    itemLogEntry(
      8003,
      "2026-03-03T10:00:00.000Z",
      "updated_estate_item",
      "lawyer",
      "Elayne Legal",
      "demo@elayne.com",
      4001,
      { before: { balance: 2_200_000 }, after: { balance: 2_400_000 } },
    ),
    itemLogEntry(
      8002,
      "2026-02-22T10:00:00.000Z",
      "added_estate_item",
      "user",
      "Robert Whitfield",
      "robert.whitfield@example.com",
      4001,
    ),
  ]),
  // Chase Checking — extracted, then accepted
  4003: mkItemLog([
    itemLogEntry(
      8005,
      "2026-02-25T10:30:00.000Z",
      "approved_estate_item",
      "user",
      "Robert Whitfield",
      "robert.whitfield@example.com",
      4003,
    ),
    itemLogEntry(
      8004,
      "2026-02-25T09:00:00.000Z",
      "added_estate_item",
      "user",
      "Robert Whitfield",
      "robert.whitfield@example.com",
      4003,
    ),
  ]),
  // Vanguard Traditional IRA — extracted, then accepted
  4007: mkItemLog([
    itemLogEntry(
      8007,
      "2026-03-01T08:00:00.000Z",
      "approved_estate_item",
      "user",
      "Robert Whitfield",
      "robert.whitfield@example.com",
      4007,
    ),
    itemLogEntry(
      8006,
      "2026-02-28T10:00:00.000Z",
      "added_estate_item",
      "user",
      "Robert Whitfield",
      "robert.whitfield@example.com",
      4007,
    ),
  ]),
  // Fidelity 401(k) — extracted, then accepted
  4009: mkItemLog([
    itemLogEntry(
      8009,
      "2026-03-02T09:00:00.000Z",
      "approved_estate_item",
      "user",
      "Robert Whitfield",
      "robert.whitfield@example.com",
      4009,
    ),
    itemLogEntry(
      8008,
      "2026-03-01T11:00:00.000Z",
      "added_estate_item",
      "user",
      "Robert Whitfield",
      "robert.whitfield@example.com",
      4009,
    ),
  ]),
  // NW Mutual Life Insurance — extracted, then accepted
  4013: mkItemLog([
    itemLogEntry(
      8011,
      "2026-03-06T08:00:00.000Z",
      "approved_estate_item",
      "user",
      "Robert Whitfield",
      "robert.whitfield@example.com",
      4013,
    ),
    itemLogEntry(
      8010,
      "2026-03-05T15:00:00.000Z",
      "added_estate_item",
      "user",
      "Robert Whitfield",
      "robert.whitfield@example.com",
      4013,
    ),
  ]),
  // KeyBank Mortgage — extracted, then accepted
  4101: mkItemLog([
    itemLogEntry(
      8013,
      "2026-03-12T12:30:00.000Z",
      "approved_estate_item",
      "user",
      "Robert Whitfield",
      "robert.whitfield@example.com",
      4101,
    ),
    itemLogEntry(
      8012,
      "2026-03-12T12:00:00.000Z",
      "added_estate_item",
      "user",
      "Robert Whitfield",
      "robert.whitfield@example.com",
      4101,
    ),
  ]),
};

// ---------------------------------------------------------------------------
// Owner relationship (Robert is surviving spouse — executor & trustee)
// ---------------------------------------------------------------------------

export const DEMO_MOCK_OWNER_RELATIONSHIP = "Spouse";

// ---------------------------------------------------------------------------
// Last activity timestamp
// ---------------------------------------------------------------------------

export const DEMO_MOCK_LAST_ACTIVITY = "2026-03-15T09:30:00.000Z";

// ===========================================================================
// ESTATE 2: Gerald Thomas Ashford (Chapel Hill, NC)
// ===========================================================================

export const DEMO_ESTATE_ID_2 = 999998;
export const DEMO_ESTATE_ID_2_STR = "999998";
export const DEMO_OWNER_USER_ID_2 = 1002;

export const DEMO_MOCK_ESTATE_2: EstateRow = {
  estate_id: DEMO_ESTATE_ID_2,
  first_name: "Gerald",
  middle_name: "Thomas",
  last_name: "Ashford",
  full_name: "Gerald Thomas Ashford",
  citizenship: null,
  occupation: null,
  employer: null,
  has_trust: null,
  date_of_birth: "1947-03-12",
  date_of_death: "2026-03-04",
  location: "Chapel Hill, NC",
  address: "612 Coker Ridge Road, Chapel Hill, NC 27516",
  place_of_death: "Chapel Hill, NC",
  phone_number: null,
  nickname: null,
  owner_user_id: DEMO_OWNER_USER_ID_2,
  lawyer_client_status: "letters_approved",
  ssn_encrypted: null,
  ein_encrypted: null,
  passport_number_encrypted: null,
  account_number_encrypted: null,
  routing_number_encrypted: null,
  funeral_home: null,
  funeral_home_reported: false,
  main_image_url: null,
  estate_type: null,
  estate_metadata: null,
  obituary_link: null,
  obituary_data: null,
  marriage_info: null,
  calendly_bookings: null,
  extended_family_heirship: false,
  probate_intake_data: null,
  tax_intake_data: null,
  tracers_response: null,
  family_tree_confirmed: false,
  family_tree_confirmed_at: null,
  family_assertions: null,
  remote_kinship_flag: false,
  created_at: "2026-03-20T10:00:00.000Z",
};

export const DEMO_MOCK_ACCOUNT_OWNER_2 = {
  full_name: "Diane Ashford-Nguyen",
  email: "diane.anguyen@example.com",
  phone_number: "(919) 555-0233",
};

const uploader2 = [
  {
    user_id: DEMO_OWNER_USER_ID_2,
    full_name: "Diane Ashford-Nguyen",
    first_name: "Diane",
    last_name: "Ashford-Nguyen",
    middle_name: null,
    nickname: null,
    email: "diane.anguyen@example.com",
  },
];

export const DEMO_MOCK_DOCUMENTS_2: MockDocument[] = [
  {
    id: 6001,
    estate_id: DEMO_ESTATE_ID_2,
    file_name: "Death_Certificate_Gerald_Ashford.pdf",
    display_name: "Death Certificate",
    document_type: "death_certificate",
    document_key: "demo/documents/ashford/Death_Certificate_Gerald_Ashford.pdf",
    extraction_status: "completed",
    file_size: 238000,
    created_at: "2026-03-04T19:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_2,
    uploaded_by: uploader2,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-04T19:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-04T19:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6002,
    estate_id: DEMO_ESTATE_ID_2,
    file_name: "Last_Will_and_Testament_GTA_2022.pdf",
    display_name: "Last Will and Testament",
    document_type: "will",
    document_key: "demo/documents/ashford/Last_Will_and_Testament_GTA_2022.pdf",
    extraction_status: "completed",
    file_size: 1640000,
    created_at: "2026-03-20T11:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_2,
    uploaded_by: uploader2,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-20T11:10:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-20T11:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6003,
    estate_id: DEMO_ESTATE_ID_2,
    file_name: "FirstCitizens_Checking_Statement_Feb2026.pdf",
    display_name: "First Citizens Checking Statement — February 2026",
    document_type: "bank_statement",
    document_key: "demo/documents/ashford/FirstCitizens_Checking_Statement_Feb2026.pdf",
    extraction_status: "completed",
    file_size: 395000,
    created_at: "2026-03-21T10:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_2,
    uploaded_by: uploader2,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-21T10:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-21T10:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6004,
    estate_id: DEMO_ESTATE_ID_2,
    file_name: "Fidelity_IRA_Statement_Q42025.pdf",
    display_name: "Fidelity IRA Statement — Q4 2025",
    document_type: "investment_account_statement",
    document_key: "demo/documents/ashford/Fidelity_IRA_Statement_Q42025.pdf",
    extraction_status: "completed",
    file_size: 521000,
    created_at: "2026-03-21T11:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_2,
    uploaded_by: uploader2,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-21T11:07:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-21T11:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6005,
    estate_id: DEMO_ESTATE_ID_2,
    file_name: "Schwab_Brokerage_Statement_Q42025.pdf",
    display_name: "Charles Schwab Brokerage Statement — Q4 2025",
    document_type: "investment_account_statement",
    document_key: "demo/documents/ashford/Schwab_Brokerage_Statement_Q42025.pdf",
    extraction_status: "completed",
    file_size: 488000,
    created_at: "2026-03-21T12:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_2,
    uploaded_by: uploader2,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-21T12:06:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-21T12:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6006,
    estate_id: DEMO_ESTATE_ID_2,
    file_name: "Truist_Savings_Statement_Feb2026.pdf",
    display_name: "Truist Savings Statement — February 2026",
    document_type: "bank_statement",
    document_key: "demo/documents/ashford/Truist_Savings_Statement_Feb2026.pdf",
    extraction_status: "completed",
    file_size: 240000,
    created_at: "2026-03-22T09:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_2,
    uploaded_by: uploader2,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-22T09:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-22T09:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6007,
    estate_id: DEMO_ESTATE_ID_2,
    file_name: "Ally_CD_Statement_2026.pdf",
    display_name: "Ally Bank CD Statement — 2026",
    document_type: "bank_statement",
    document_key: "demo/documents/ashford/Ally_CD_Statement_2026.pdf",
    extraction_status: "completed",
    file_size: 260000,
    created_at: "2026-03-22T10:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_2,
    uploaded_by: uploader2,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-22T10:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-22T10:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6008,
    estate_id: DEMO_ESTATE_ID_2,
    file_name: "FirstCitizens_HELOC_Statement_Feb2026.pdf",
    display_name: "First Citizens HELOC Statement — February 2026",
    document_type: "bank_statement",
    document_key: "demo/documents/ashford/FirstCitizens_HELOC_Statement_Feb2026.pdf",
    extraction_status: "completed",
    file_size: 260000,
    created_at: "2026-03-22T11:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_2,
    uploaded_by: uploader2,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-22T11:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-22T11:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6009,
    estate_id: DEMO_ESTATE_ID_2,
    file_name: "Truist_Visa_Statement_Feb2026.pdf",
    display_name: "Truist Visa Statement — February 2026",
    document_type: "credit_card_statement",
    document_key: "demo/documents/ashford/Truist_Visa_Statement_Feb2026.pdf",
    extraction_status: "completed",
    file_size: 280000,
    created_at: "2026-03-22T12:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_2,
    uploaded_by: uploader2,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-22T12:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-22T12:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6010,
    estate_id: DEMO_ESTATE_ID_2,
    file_name: "NC529_Account_Statement_2025.pdf",
    display_name: "NC 529 Plan Statement — 2025",
    document_type: "investment_account_statement",
    document_key: "demo/documents/ashford/NC529_Account_Statement_2025.pdf",
    extraction_status: "completed",
    file_size: 250000,
    created_at: "2026-03-23T09:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_2,
    uploaded_by: uploader2,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-23T09:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-23T09:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6011,
    estate_id: DEMO_ESTATE_ID_2,
    file_name: "StateFarm_Life_Policy_SF8844721.pdf",
    display_name: "State Farm Whole Life Policy (SF-88-4472-1)",
    document_type: "life_insurance_policy",
    document_key: "demo/documents/ashford/StateFarm_Life_Policy_SF8844721.pdf",
    extraction_status: "completed",
    file_size: 260000,
    created_at: "2026-03-23T10:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_2,
    uploaded_by: uploader2,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-23T10:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-23T10:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6012,
    estate_id: DEMO_ESTATE_ID_2,
    file_name: "2025_Federal_Tax_Return_1040_GTA.pdf",
    display_name: "2025 Federal Tax Return (Form 1040)",
    document_type: "tax_return",
    document_key: "demo/documents/ashford/2025_Federal_Tax_Return_1040_GTA.pdf",
    extraction_status: "completed",
    file_size: 320000,
    created_at: "2026-03-23T11:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_2,
    uploaded_by: uploader2,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-23T11:06:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-23T11:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6013,
    estate_id: DEMO_ESTATE_ID_2,
    file_name: "Last_Will_and_Testament_MEW_2023.pdf",
    display_name: "Last Will and Testament — Margaret E. Whitfield (2023)",
    document_type: "will",
    document_key: "demo/documents/ashford/Last_Will_and_Testament_MEW_2023.pdf",
    extraction_status: "completed",
    file_size: 380000,
    created_at: "2026-03-23T12:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_2,
    uploaded_by: uploader2,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-03-23T12:07:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-03-23T12:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
];

export const DEMO_MOCK_CONTACTS_2: MockContact[] = [
  {
    id: 7001,
    estate_id: DEMO_ESTATE_ID_2,
    estate_owner_id: DEMO_OWNER_USER_ID_2,
    user_id: DEMO_OWNER_USER_ID_2,
    first_name: "Diane",
    last_name: "Ashford-Nguyen",
    middle_name: null,
    nickname: null,
    email: "diane.anguyen@example.com",
    phone: "(919) 555-0233",
    address: "612 Coker Ridge Road, Chapel Hill, NC 27516",
    relationship: "Child",
    legal_role: ["executor"],
    profile_picture: null,
    invited: true,
    is_hidden: false,
    source_document_id: 6002,
    extracted_from_document: true,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-03-20T11:10:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-03-20T11:10:00.000Z",
    source_document: {
      id: 6002,
      file_name: "Last_Will_and_Testament_GTA_2022.pdf",
      display_name: "Last Will and Testament",
      document_type: "will",
      created_at: "2026-03-20T11:00:00.000Z",
    },
  },
  {
    id: 7002,
    estate_id: DEMO_ESTATE_ID_2,
    estate_owner_id: DEMO_OWNER_USER_ID_2,
    user_id: DEMO_OWNER_USER_ID_2,
    first_name: "Marcus",
    last_name: "Ashford",
    middle_name: null,
    nickname: null,
    email: "marcus.ashford@example.com",
    phone: "(704) 555-0187",
    address: null,
    relationship: "Child",
    legal_role: ["successor_executor", "beneficiary"],
    profile_picture: null,
    invited: true,
    is_hidden: false,
    source_document_id: 6002,
    extracted_from_document: true,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-03-20T11:10:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-03-20T11:10:00.000Z",
    source_document: {
      id: 6002,
      file_name: "Last_Will_and_Testament_GTA_2022.pdf",
      display_name: "Last Will and Testament",
      document_type: "will",
      created_at: "2026-03-20T11:00:00.000Z",
    },
  },
  {
    id: 7003,
    estate_id: DEMO_ESTATE_ID_2,
    estate_owner_id: DEMO_OWNER_USER_ID_2,
    user_id: DEMO_OWNER_USER_ID_2,
    first_name: "Laura",
    last_name: "Ashford-Pruitt",
    middle_name: null,
    nickname: null,
    email: "laura.pruitt@example.com",
    phone: "(828) 555-0341",
    address: null,
    relationship: "Child",
    legal_role: ["beneficiary"],
    profile_picture: null,
    invited: true,
    is_hidden: false,
    source_document_id: 6002,
    extracted_from_document: true,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-03-20T11:10:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-03-20T11:10:00.000Z",
    source_document: {
      id: 6002,
      file_name: "Last_Will_and_Testament_GTA_2022.pdf",
      display_name: "Last Will and Testament",
      document_type: "will",
      created_at: "2026-03-20T11:00:00.000Z",
    },
  },
  {
    id: 7004,
    estate_id: DEMO_ESTATE_ID_2,
    estate_owner_id: DEMO_OWNER_USER_ID_2,
    user_id: DEMO_OWNER_USER_ID_2,
    first_name: "Peter",
    last_name: "Nguyen",
    middle_name: null,
    nickname: null,
    email: null,
    phone: "(919) 555-0233",
    address: null,
    relationship: "Son-in-law",
    legal_role: null,
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-03-22T09:00:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-03-22T09:00:00.000Z",
    source_document: null,
  },
  {
    id: 7005,
    estate_id: DEMO_ESTATE_ID_2,
    estate_owner_id: DEMO_OWNER_USER_ID_2,
    user_id: DEMO_OWNER_USER_ID_2,
    first_name: "Claire",
    last_name: "Nguyen",
    middle_name: null,
    nickname: null,
    email: "claire.nguyen@ncsu.example.edu",
    phone: "(919) 555-0472",
    address: null,
    relationship: "Grandchild",
    legal_role: ["beneficiary"],
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-03-22T09:00:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-03-22T09:00:00.000Z",
    source_document: null,
  },
  {
    id: 7006,
    estate_id: DEMO_ESTATE_ID_2,
    estate_owner_id: DEMO_OWNER_USER_ID_2,
    user_id: DEMO_OWNER_USER_ID_2,
    first_name: "Wesley",
    last_name: "Nguyen",
    middle_name: null,
    nickname: null,
    email: "wes.n2008@example.com",
    phone: null,
    address: null,
    relationship: "Grandchild",
    legal_role: ["beneficiary"],
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-03-22T09:00:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-03-22T09:00:00.000Z",
    source_document: null,
  },
  {
    id: 7007,
    estate_id: DEMO_ESTATE_ID_2,
    estate_owner_id: DEMO_OWNER_USER_ID_2,
    user_id: DEMO_OWNER_USER_ID_2,
    first_name: "Tessa",
    last_name: "Ashford",
    middle_name: null,
    nickname: null,
    email: null,
    phone: null,
    address: null,
    relationship: "Grandchild",
    legal_role: ["beneficiary"],
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-03-22T09:00:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-03-22T09:00:00.000Z",
    source_document: null,
  },
];

export const DEMO_MOCK_INVITATIONS_2: EstateInvitationRow[] = [
  {
    id: 3101,
    estate_id: DEMO_ESTATE_ID_2,
    email: "diane.anguyen@example.com",
    role: "executor",
    status: "accepted",
    invitation_token: "demo-token-diane",
    invited_by_user_id: DEMO_USER_ID,
    is_attorney: false,
    created_at: "2026-03-20T10:30:00.000Z",
    updated_at: "2026-03-21T08:00:00.000Z",
    expires_at: "2026-04-20T10:30:00.000Z",
  },
  {
    id: 3102,
    estate_id: DEMO_ESTATE_ID_2,
    email: "marcus.ashford@example.com",
    role: "beneficiary",
    status: "pending",
    invitation_token: "demo-token-marcus",
    invited_by_user_id: DEMO_USER_ID,
    is_attorney: false,
    created_at: "2026-03-22T14:00:00.000Z",
    updated_at: "2026-03-22T14:00:00.000Z",
    expires_at: "2026-04-22T14:00:00.000Z",
  },
  {
    id: 3103,
    estate_id: DEMO_ESTATE_ID_2,
    email: "laura.pruitt@example.com",
    role: "beneficiary",
    status: "pending",
    invitation_token: "demo-token-laura",
    invited_by_user_id: DEMO_USER_ID,
    is_attorney: false,
    created_at: "2026-03-22T14:30:00.000Z",
    updated_at: "2026-03-22T14:30:00.000Z",
    expires_at: "2026-04-22T14:30:00.000Z",
  },
];

export const DEMO_MOCK_JOINED_USERS_2 = [
  { user_id: DEMO_OWNER_USER_ID_2, email: "diane.anguyen@example.com" },
];

const now2 = "2026-03-22T10:00:00.000Z";

const baseItem2 = (
  fields: Partial<EstateItem> &
    Pick<EstateItem, "id" | "category" | "item_name" | "balance" | "classification">,
): EstateItem => ({
  estate_id: DEMO_ESTATE_ID_2,
  item_type: undefined,
  ownership: null,
  created_at: now2,
  updated_at: now2,
  created_by_user_id: DEMO_OWNER_USER_ID_2,
  last_updated_date: now2,
  notes: undefined,
  plaid_connection_id: null,
  plaid_account_id: null,
  plaid_account_mask: null,
  plaid_last_refreshed_at: null,
  plaid_institution_logo: null,
  source_document_id: null,
  extracted_from_document: false,
  extraction_status: "approved",
  open_date: null,
  identifiers: {},
  metadata: {},
  is_estimated: false,
  balance_estimate_range: null,
  value_label: null,
  status_flag: null,
  linked_item_id: null,
  source_document: null,
  document_item_references: null,
  ...fields,
});

const srcAshfordFirstCitizens = mkDoc(
  6003,
  "FirstCitizens_Checking_Statement_Feb2026.pdf",
  "First Citizens Checking Statement — February 2026",
  "bank_statement",
  "2026-03-21T10:00:00.000Z",
);

const srcAshfordFidelityIRA = mkDoc(
  6004,
  "Fidelity_IRA_Statement_Q42025.pdf",
  "Fidelity IRA Statement — Q4 2025",
  "investment_account_statement",
  "2026-03-21T11:00:00.000Z",
);

const srcAshfordSchwab = mkDoc(
  6005,
  "Schwab_Brokerage_Statement_Q42025.pdf",
  "Charles Schwab Brokerage Statement — Q4 2025",
  "investment_account_statement",
  "2026-03-21T12:00:00.000Z",
);

const mockAssets2: EstateItem[] = [
  baseItem2({
    id: 5001,
    category: "Properties",
    item_name: "Chapel Hill Residence — 612 Coker Ridge Road",
    item_type: "Residential",
    balance: 685_000,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: { address: "612 Coker Ridge Road, Chapel Hill, NC 27516" },
  }),
  baseItem2({
    id: 5002,
    category: "Properties",
    item_name: "Undeveloped Land — 0 Beaver Creek Road, Pittsboro",
    item_type: "Rental properties",
    balance: 48_000,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: { address: "0 Beaver Creek Road, Pittsboro, NC" },
  }),
  baseItem2({
    id: 5003,
    category: "Bank accounts",
    item_name: "First Citizens Checking (\u20132417)",
    item_type: "Checking accounts",
    balance: 18_450.33,
    classification: "asset",
    ownership: "pod_tod",
    identifiers: { institution: "First Citizens", account_type: "checking", account_last4: "2417" },
    source_document_id: 6003,
    extracted_from_document: true,
    source_document: srcAshfordFirstCitizens,
  }),
  baseItem2({
    id: 5004,
    category: "Bank accounts",
    item_name: "Truist Savings (\u20138809)",
    item_type: "Savings accounts",
    balance: 62_300.18,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: { institution: "Truist", account_type: "savings", account_last4: "8809" },
  }),
  baseItem2({
    id: 5005,
    category: "Bank accounts",
    item_name: "Ally Bank 18-Month CD (CD\u20135531)",
    item_type: "Certificates of Deposit (CDs)",
    balance: 40_000,
    classification: "asset",
    ownership: "pod_tod",
    identifiers: { institution: "Ally Bank", account_type: "cd", account_last4: "5531" },
  }),
  baseItem2({
    id: 5006,
    category: "Retirement accounts",
    item_name: "Fidelity Traditional IRA",
    item_type: "Traditional IRA",
    balance: 232_640,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: { institution: "Fidelity", account_type: "ira" },
    source_document_id: 6004,
    extracted_from_document: true,
    source_document: srcAshfordFidelityIRA,
  }),
  baseItem2({
    id: 5007,
    category: "Retirement accounts",
    item_name: "Fidelity Roth IRA",
    item_type: "Roth IRA",
    balance: 38_750,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: { institution: "Fidelity", account_type: "roth_ira" },
    source_document_id: 6004,
    extracted_from_document: true,
    source_document: srcAshfordFidelityIRA,
  }),
  baseItem2({
    id: 5008,
    category: "Brokerage accounts",
    item_name: "Charles Schwab Brokerage",
    item_type: "Individual brokerage account",
    balance: 118_400,
    classification: "asset",
    ownership: "pod_tod",
    identifiers: { institution: "Charles Schwab", account_type: "brokerage" },
    source_document_id: 6005,
    extracted_from_document: true,
    source_document: srcAshfordSchwab,
  }),
  baseItem2({
    id: 5009,
    category: "Tax-advantaged savings",
    item_name: "NC 529 Plans \u2014 3 Beneficiaries",
    item_type: "529 Education Account",
    balance: 60_000,
    classification: "asset",
    ownership: "pod_tod",
    identifiers: { institution: "NC 529" },
  }),
  baseItem2({
    id: 5010,
    category: "Life insurance",
    item_name: "State Farm Whole Life (SF-88-4472-1)",
    item_type: "Whole or universal life insurance",
    balance: 100_000,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: {
      institution: "State Farm",
      provider: "State Farm Life Insurance Company",
      policy_type: "whole",
      policy_number: "SF-88-4472-1",
    },
  }),
  baseItem2({
    id: 5011,
    category: "Vehicles",
    item_name: "2019 Toyota Highlander",
    item_type: "Cars and motorcycles",
    balance: 24_500,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: { make: "Toyota", model: "Highlander", year: "2019" },
  }),
  baseItem2({
    id: 5012,
    category: "Valuables",
    item_name: "1974 MGB (Restoration Project)",
    item_type: "Other valuables",
    balance: 8_000,
    classification: "asset",
    is_estimated: true,
  }),
  baseItem2({
    id: 5013,
    category: "Valuables",
    item_name: "Woodworking Shop & Tools",
    item_type: "Other valuables",
    balance: 14_000,
    classification: "asset",
    is_estimated: true,
  }),
  baseItem2({
    id: 5014,
    category: "Valuables",
    item_name: "Coin Collection",
    item_type: "Collections (coins, stamps, etc.)",
    balance: 18_500,
    classification: "asset",
    is_estimated: true,
  }),
  baseItem2({
    id: 5015,
    category: "Valuables",
    item_name: "Household Furnishings",
    item_type: "Antiques & Furniture",
    balance: 35_000,
    classification: "asset",
    is_estimated: true,
  }),
  baseItem2({
    id: 5016,
    category: "Valuables",
    item_name: "Other Personal Effects",
    item_type: "Other valuables",
    balance: 6_000,
    classification: "asset",
  }),
];

const mockDebts2: EstateItem[] = [
  baseItem2({
    id: 5101,
    category: "Secured debts",
    item_name: "First Citizens HELOC \u2014 612 Coker Ridge Road",
    item_type: "Home equity loans",
    balance: 38_214.67,
    classification: "debt",
    identifiers: { institution: "First Citizens", loan_type: "heloc" },
    linked_item_id: 5001,
  }),
  baseItem2({
    id: 5102,
    category: "Unsecured Debts",
    item_name: "Truist Visa (\u20134471)",
    item_type: "Credit card debt",
    balance: 2_183.42,
    classification: "debt",
    identifiers: { institution: "Truist", account_type: "credit_card", account_last4: "4471" },
  }),
];

const totalAssets2 = mockAssets2
  .filter((a) => !a.value_label)
  .reduce((sum, a) => sum + a.balance, 0);
const totalDebts2 = mockDebts2.filter((d) => !d.value_label).reduce((sum, d) => sum + d.balance, 0);

const categoryMap2: Record<string, { value: number; accountCount: number }> = {};
mockAssets2
  .filter((a) => !a.value_label)
  .forEach((item) => {
    if (!categoryMap2[item.category]) categoryMap2[item.category] = { value: 0, accountCount: 0 };
    categoryMap2[item.category].value += item.balance;
    categoryMap2[item.category].accountCount += 1;
  });

const mockCategories2 = Object.entries(categoryMap2).map(([category, data]) => ({
  category,
  value: data.value,
  accountCount: data.accountCount,
  percentage: totalAssets2 > 0 ? (data.value / totalAssets2) * 100 : 0,
  icon: "",
  ...(CATEGORY_COLORS[category] ?? {
    colorClass: "bg-gray-400",
    bgOpacityClass: "bg-gray-400/10",
    textColorClass: "text-gray-400",
  }),
}));

export const DEMO_MOCK_ESTATE_ITEMS_2: EstateItemsResponse = {
  assets: mockAssets2,
  debts: mockDebts2,
  summary: {
    totalAssets: totalAssets2,
    totalDebts: totalDebts2,
    netValue: totalAssets2 - totalDebts2,
    hasItems: true,
    categories: mockCategories2,
    pendingValuationCount: 0,
  },
};

export const DEMO_MOCK_ITEM_BENEFICIARIES_2: Record<number, ItemBeneficiary[]> = {
  5006: [
    mkBeneficiary(9001, 7001, "Diane Ashford-Nguyen", "Child", null, 1, 3, false),
    mkBeneficiary(9002, 7002, "Marcus Ashford", "Child", null, 1, 3, false),
    mkBeneficiary(9003, 7003, "Laura Ashford-Pruitt", "Child", null, 1, 3, false),
  ],
  5007: [
    mkBeneficiary(9004, 7001, "Diane Ashford-Nguyen", "Child", null, 1, 3, false),
    mkBeneficiary(9005, 7002, "Marcus Ashford", "Child", null, 1, 3, false),
    mkBeneficiary(9006, 7003, "Laura Ashford-Pruitt", "Child", null, 1, 3, false),
  ],
  5009: [
    mkBeneficiary(9007, 7005, "Claire Nguyen", "Grandchild", null, 1, 3, false),
    mkBeneficiary(9008, 7006, "Wesley Nguyen", "Grandchild", null, 1, 3, false),
    mkBeneficiary(9009, 7007, "Tessa Ashford", "Grandchild", null, 1, 3, false),
  ],
  5010: [
    mkBeneficiary(9010, 7001, "Diane Ashford-Nguyen", "Child", 33.34, null, null, false),
    mkBeneficiary(9011, 7002, "Marcus Ashford", "Child", 33.33, null, null, false),
    mkBeneficiary(9012, 7003, "Laura Ashford-Pruitt", "Child", 33.33, null, null, false),
  ],
};

export const DEMO_MOCK_ITEM_DOCUMENT_REFS_2: Record<number, DocumentReference[]> = {
  5003: [
    mkDocRef(
      10001,
      6003,
      5003,
      "approved",
      "FirstCitizens_Checking_Statement_Feb2026.pdf",
      "First Citizens Checking Statement — February 2026",
      "bank_statement",
      "2026-03-21T10:00:00.000Z",
      "2026-03-21T10:30:00.000Z",
    ),
  ],
  5006: [
    mkDocRef(
      10002,
      6004,
      5006,
      "approved",
      "Fidelity_IRA_Statement_Q42025.pdf",
      "Fidelity IRA Statement — Q4 2025",
      "investment_account_statement",
      "2026-03-21T11:00:00.000Z",
      "2026-03-21T11:30:00.000Z",
    ),
  ],
  5007: [
    mkDocRef(
      10003,
      6004,
      5007,
      "approved",
      "Fidelity_IRA_Statement_Q42025.pdf",
      "Fidelity IRA Statement — Q4 2025",
      "investment_account_statement",
      "2026-03-21T11:00:00.000Z",
      "2026-03-21T11:30:00.000Z",
    ),
  ],
  5008: [
    mkDocRef(
      10004,
      6005,
      5008,
      "approved",
      "Schwab_Brokerage_Statement_Q42025.pdf",
      "Charles Schwab Brokerage Statement — Q4 2025",
      "investment_account_statement",
      "2026-03-21T12:00:00.000Z",
      "2026-03-21T12:30:00.000Z",
    ),
  ],
};

const logEntry2 = (
  id: number,
  created_at: string,
  action_type: ActionType,
  actor_type: ActorType,
  actor_name: string,
  actor_email: string,
  entity_type: string | null = null,
  metadata: Record<string, unknown> = {},
): ActivityLogEntry => ({
  id,
  created_at,
  estate_id: DEMO_ESTATE_ID_2,
  action_type,
  actor_type,
  user_id: actor_type === "lawyer" ? DEMO_USER_ID : DEMO_OWNER_USER_ID_2,
  admin_id: null,
  entity_type,
  entity_id: null,
  metadata,
  actor_name,
  actor_email,
});

const mockActivityEntries2: ActivityLogEntry[] = [
  logEntry2(
    11007,
    "2026-03-22T10:00:00.000Z",
    "added_estate_item",
    "user",
    "Diane Ashford-Nguyen",
    "diane.anguyen@example.com",
    "estate_item",
    { item_name: "Coin Collection", category: "Valuables", balance: 18500 },
  ),
  logEntry2(
    11006,
    "2026-03-21T12:00:00.000Z",
    "uploaded_document",
    "user",
    "Diane Ashford-Nguyen",
    "diane.anguyen@example.com",
    "document",
    {
      document_type: "investment_account_statement",
      file_name: "Schwab_Brokerage_Statement_Q42025.pdf",
    },
  ),
  logEntry2(
    11005,
    "2026-03-21T11:00:00.000Z",
    "uploaded_document",
    "user",
    "Diane Ashford-Nguyen",
    "diane.anguyen@example.com",
    "document",
    {
      document_type: "investment_account_statement",
      file_name: "Fidelity_IRA_Statement_Q42025.pdf",
    },
  ),
  logEntry2(
    11004,
    "2026-03-21T10:00:00.000Z",
    "uploaded_document",
    "user",
    "Diane Ashford-Nguyen",
    "diane.anguyen@example.com",
    "document",
    { document_type: "bank_statement", file_name: "FirstCitizens_Checking_Statement_Feb2026.pdf" },
  ),
  logEntry2(
    11003,
    "2026-03-20T12:00:00.000Z",
    "added_estate_item",
    "user",
    "Diane Ashford-Nguyen",
    "diane.anguyen@example.com",
    "estate_item",
    { item_name: "Chapel Hill Residence", category: "Properties", balance: 685000 },
  ),
  logEntry2(
    11002,
    "2026-03-20T11:00:00.000Z",
    "uploaded_document",
    "user",
    "Diane Ashford-Nguyen",
    "diane.anguyen@example.com",
    "document",
    { document_type: "will", file_name: "Last_Will_and_Testament_GTA_2022.pdf" },
  ),
  logEntry2(
    11001,
    "2026-03-04T19:00:00.000Z",
    "uploaded_document",
    "user",
    "Diane Ashford-Nguyen",
    "diane.anguyen@example.com",
    "document",
    { document_type: "death_certificate", file_name: "Death_Certificate_Gerald_Ashford.pdf" },
  ),
];

export const DEMO_MOCK_ACTIVITY_LOG_2: InfiniteData<ActivityLogPage> = {
  pages: [{ entries: mockActivityEntries2, nextCursor: null }],
  pageParams: [null],
};

const itemLogEntry2 = (
  id: number,
  created_at: string,
  action_type: ActionType,
  actor_type: ActorType,
  actor_name: string,
  actor_email: string,
  itemId: number,
  metadata: Record<string, unknown> = {},
): ActivityLogEntry => ({
  id,
  created_at,
  estate_id: DEMO_ESTATE_ID_2,
  action_type,
  actor_type,
  user_id: actor_type === "lawyer" ? DEMO_USER_ID : DEMO_OWNER_USER_ID_2,
  admin_id: null,
  entity_type: "estate_item",
  entity_id: String(itemId),
  metadata,
  actor_name,
  actor_email,
});

export const DEMO_MOCK_ITEM_ACTIVITY_LOGS_2: Record<number, InfiniteData<ActivityLogPage>> = {
  5001: mkItemLog([
    itemLogEntry2(
      11101,
      "2026-03-20T12:00:00.000Z",
      "added_estate_item",
      "user",
      "Diane Ashford-Nguyen",
      "diane.anguyen@example.com",
      5001,
    ),
  ]),
  5006: mkItemLog([
    itemLogEntry2(
      11103,
      "2026-03-21T11:30:00.000Z",
      "approved_estate_item",
      "user",
      "Diane Ashford-Nguyen",
      "diane.anguyen@example.com",
      5006,
    ),
    itemLogEntry2(
      11102,
      "2026-03-21T11:00:00.000Z",
      "added_estate_item",
      "user",
      "Diane Ashford-Nguyen",
      "diane.anguyen@example.com",
      5006,
    ),
  ]),
  5008: mkItemLog([
    itemLogEntry2(
      11105,
      "2026-03-21T12:30:00.000Z",
      "approved_estate_item",
      "user",
      "Diane Ashford-Nguyen",
      "diane.anguyen@example.com",
      5008,
    ),
    itemLogEntry2(
      11104,
      "2026-03-21T12:00:00.000Z",
      "added_estate_item",
      "user",
      "Diane Ashford-Nguyen",
      "diane.anguyen@example.com",
      5008,
    ),
  ]),
};

export const DEMO_MOCK_OWNER_RELATIONSHIP_2 = "Child";

export const DEMO_MOCK_LAST_ACTIVITY_2 = "2026-03-22T10:00:00.000Z";

// ===========================================================================
// ESTATE 3: Sylvia Marie Delgado (Austin, TX)
// ===========================================================================

export const DEMO_ESTATE_ID_3 = 999997;
export const DEMO_ESTATE_ID_3_STR = "999997";
export const DEMO_OWNER_USER_ID_3 = 1003;

export const DEMO_MOCK_ESTATE_3: EstateRow = {
  estate_id: DEMO_ESTATE_ID_3,
  first_name: "Sylvia",
  middle_name: "Marie",
  last_name: "Delgado",
  full_name: "Sylvia Marie Delgado",
  citizenship: null,
  occupation: null,
  employer: null,
  has_trust: null,
  date_of_birth: "1959-07-30",
  date_of_death: "2026-01-22",
  location: "Austin, TX",
  address: "2604 Larkspur Valley Drive, Austin, TX 78704",
  place_of_death: "Austin, TX",
  phone_number: null,
  nickname: null,
  owner_user_id: DEMO_OWNER_USER_ID_3,
  lawyer_client_status: "in_progress",
  ssn_encrypted: null,
  ein_encrypted: null,
  passport_number_encrypted: null,
  account_number_encrypted: null,
  routing_number_encrypted: null,
  funeral_home: null,
  funeral_home_reported: false,
  main_image_url: null,
  estate_type: null,
  estate_metadata: null,
  obituary_link: null,
  obituary_data: null,
  marriage_info: null,
  calendly_bookings: null,
  extended_family_heirship: false,
  probate_intake_data: null,
  tax_intake_data: null,
  tracers_response: null,
  family_tree_confirmed: false,
  family_tree_confirmed_at: null,
  family_assertions: null,
  remote_kinship_flag: false,
  created_at: "2026-04-01T10:00:00.000Z",
};

export const DEMO_MOCK_ACCOUNT_OWNER_3 = {
  full_name: "Monica Delgado-Reyes",
  email: "monica.dreyes@example.com",
  phone_number: "(512) 555-0296",
};

const uploader3 = [
  {
    user_id: DEMO_OWNER_USER_ID_3,
    full_name: "Monica Delgado-Reyes",
    first_name: "Monica",
    last_name: "Delgado-Reyes",
    middle_name: null,
    nickname: null,
    email: "monica.dreyes@example.com",
  },
];

export const DEMO_MOCK_DOCUMENTS_3: MockDocument[] = [
  {
    id: 6101,
    estate_id: DEMO_ESTATE_ID_3,
    file_name: "Death_Certificate_Sylvia_Delgado.pdf",
    display_name: "Death Certificate",
    document_type: "death_certificate",
    document_key: "demo/documents/delgado/Death_Certificate_Sylvia_Delgado.pdf",
    extraction_status: "completed",
    file_size: 241000,
    created_at: "2026-01-22T20:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_3,
    uploaded_by: uploader3,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-01-22T20:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-01-22T20:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6102,
    estate_id: DEMO_ESTATE_ID_3,
    file_name: "Frost_Checking_Statement_Jan2026.pdf",
    display_name: "Frost Bank Checking Statement — January 2026",
    document_type: "bank_statement",
    document_key: "demo/documents/delgado/Frost_Checking_Statement_Jan2026.pdf",
    extraction_status: "completed",
    file_size: 410000,
    created_at: "2026-04-02T10:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_3,
    uploaded_by: uploader3,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-04-02T10:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-04-02T10:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6103,
    estate_id: DEMO_ESTATE_ID_3,
    file_name: "Schwab_SEPIRA_Statement_Q42025.pdf",
    display_name: "Charles Schwab SEP-IRA Statement — Q4 2025",
    document_type: "investment_account_statement",
    document_key: "demo/documents/delgado/Schwab_SEPIRA_Statement_Q42025.pdf",
    extraction_status: "completed",
    file_size: 563000,
    created_at: "2026-04-02T11:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_3,
    uploaded_by: uploader3,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-04-02T11:07:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-04-02T11:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6104,
    estate_id: DEMO_ESTATE_ID_3,
    file_name: "Vanguard_Brokerage_Statement_Q42025.pdf",
    display_name: "Vanguard Individual Brokerage Statement — Q4 2025",
    document_type: "investment_account_statement",
    document_key: "demo/documents/delgado/Vanguard_Brokerage_Statement_Q42025.pdf",
    extraction_status: "completed",
    file_size: 498000,
    created_at: "2026-04-02T12:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_3,
    uploaded_by: uploader3,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-04-02T12:06:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-04-02T12:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6105,
    estate_id: DEMO_ESTATE_ID_3,
    file_name: "Amplify_Mortgage_Statement_Jan2026.pdf",
    display_name: "Amplify Credit Union Mortgage Statement — January 2026",
    document_type: "mortgage_statement",
    document_key: "demo/documents/delgado/Amplify_Mortgage_Statement_Jan2026.pdf",
    extraction_status: "completed",
    file_size: 302000,
    created_at: "2026-04-03T10:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_3,
    uploaded_by: uploader3,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-04-03T10:04:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-04-03T10:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6106,
    estate_id: DEMO_ESTATE_ID_3,
    file_name: "UFCU_Savings_Statement_Jan2026.pdf",
    display_name: "UFCU Savings Statement — January 2026",
    document_type: "bank_statement",
    document_key: "demo/documents/delgado/UFCU_Savings_Statement_Jan2026.pdf",
    extraction_status: "completed",
    file_size: 240000,
    created_at: "2026-04-03T11:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_3,
    uploaded_by: uploader3,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-04-03T11:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-04-03T11:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6107,
    estate_id: DEMO_ESTATE_ID_3,
    file_name: "Coinbase_Statement_Jan2026.pdf",
    display_name: "Coinbase Account Statement — January 2026",
    document_type: "investment_account_statement",
    document_key: "demo/documents/delgado/Coinbase_Statement_Jan2026.pdf",
    extraction_status: "completed",
    file_size: 270000,
    created_at: "2026-04-03T12:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_3,
    uploaded_by: uploader3,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-04-03T12:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-04-03T12:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6108,
    estate_id: DEMO_ESTATE_ID_3,
    file_name: "Principal_Life_Policy_PL4490832.pdf",
    display_name: "Principal Life Term Policy (PL-44-9083-2)",
    document_type: "life_insurance_policy",
    document_key: "demo/documents/delgado/Principal_Life_Policy_PL4490832.pdf",
    extraction_status: "completed",
    file_size: 250000,
    created_at: "2026-04-04T09:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_3,
    uploaded_by: uploader3,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-04-04T09:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-04-04T09:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6109,
    estate_id: DEMO_ESTATE_ID_3,
    file_name: "DGM_LLC_Company_Agreement.pdf",
    display_name: "DGM LLC Company Agreement",
    document_type: "other",
    document_key: "demo/documents/delgado/DGM_LLC_Company_Agreement.pdf",
    extraction_status: "completed",
    file_size: 320000,
    created_at: "2026-04-04T10:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_3,
    uploaded_by: uploader3,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-04-04T10:06:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-04-04T10:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6110,
    estate_id: DEMO_ESTATE_ID_3,
    file_name: "DGM_LLC_2025_Schedule_K1.pdf",
    display_name: "Schedule K-1 (Form 1120-S) — DGM LLC 2025",
    document_type: "tax_return",
    document_key: "demo/documents/delgado/DGM_LLC_2025_Schedule_K1.pdf",
    extraction_status: "completed",
    file_size: 270000,
    created_at: "2026-04-04T11:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_3,
    uploaded_by: uploader3,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-04-04T11:05:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-04-04T11:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
  {
    id: 6111,
    estate_id: DEMO_ESTATE_ID_3,
    file_name: "2025_Federal_Tax_Return_1040_SMD.pdf",
    display_name: "2025 Federal Tax Return (Form 1040)",
    document_type: "tax_return",
    document_key: "demo/documents/delgado/2025_Federal_Tax_Return_1040_SMD.pdf",
    extraction_status: "completed",
    file_size: 320000,
    created_at: "2026-04-04T12:00:00.000Z",
    uploaded_by_user_id: DEMO_OWNER_USER_ID_3,
    uploaded_by: uploader3,
    confidence_breakdown: null,
    document_extraction: null,
    duplicate_id: null,
    extraction_completed_at: "2026-04-04T12:06:00.000Z",
    extraction_error: null,
    extraction_model: null,
    extraction_prompt_version: null,
    extraction_started_at: "2026-04-04T12:01:00.000Z",
    file_hash: null,
    is_confirmation: null,
    olive_job_id: null,
    reducto_general_extraction: null,
    reducto_specific_extraction: null,
    smart_summary: null,
    specific_doc_citations: null,
    specific_doc_extraction: null,
    tax_transcript_job_id: null,
    tax_transcript_pipeline: null,
    upload_session_id: null,
    folder_id: null,
  },
];

export const DEMO_MOCK_CONTACTS_3: MockContact[] = [
  {
    id: 8001,
    estate_id: DEMO_ESTATE_ID_3,
    estate_owner_id: DEMO_OWNER_USER_ID_3,
    user_id: DEMO_OWNER_USER_ID_3,
    first_name: "Monica",
    last_name: "Delgado-Reyes",
    middle_name: null,
    nickname: null,
    email: "monica.dreyes@example.com",
    phone: "(512) 555-0296",
    address: "2604 Larkspur Valley Drive, Austin, TX 78704",
    relationship: "Child",
    legal_role: ["administrator"],
    profile_picture: null,
    invited: true,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-04-01T10:30:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-04-01T10:30:00.000Z",
    source_document: null,
  },
  {
    id: 8002,
    estate_id: DEMO_ESTATE_ID_3,
    estate_owner_id: DEMO_OWNER_USER_ID_3,
    user_id: DEMO_OWNER_USER_ID_3,
    first_name: "Daniel",
    last_name: "Delgado",
    middle_name: null,
    nickname: null,
    email: "daniel.delgado@example.com",
    phone: "(720) 555-0158",
    address: null,
    relationship: "Child",
    legal_role: ["beneficiary"],
    profile_picture: null,
    invited: true,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-04-01T11:00:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-04-01T11:00:00.000Z",
    source_document: null,
    is_distributee: false,
    priority_tier: null,
    can_serve: false,
  },
  {
    id: 8003,
    estate_id: DEMO_ESTATE_ID_3,
    estate_owner_id: DEMO_OWNER_USER_ID_3,
    user_id: DEMO_OWNER_USER_ID_3,
    first_name: "Carlos",
    last_name: "Reyes",
    middle_name: null,
    nickname: null,
    email: null,
    phone: "(512) 555-0296",
    address: null,
    relationship: "Son-in-law",
    legal_role: null,
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-04-02T09:00:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-04-02T09:00:00.000Z",
    source_document: null,
  },
  {
    id: 8004,
    estate_id: DEMO_ESTATE_ID_3,
    estate_owner_id: DEMO_OWNER_USER_ID_3,
    user_id: DEMO_OWNER_USER_ID_3,
    first_name: "Alex",
    last_name: "Reyes",
    middle_name: null,
    nickname: null,
    email: null,
    phone: null,
    address: null,
    relationship: "Grandchild",
    legal_role: null,
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-04-02T09:00:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-04-02T09:00:00.000Z",
    source_document: null,
  },
  {
    id: 8005,
    estate_id: DEMO_ESTATE_ID_3,
    estate_owner_id: DEMO_OWNER_USER_ID_3,
    user_id: DEMO_OWNER_USER_ID_3,
    first_name: "Gabriela",
    last_name: "Reyes",
    middle_name: null,
    nickname: null,
    email: null,
    phone: null,
    address: null,
    relationship: "Grandchild",
    legal_role: null,
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-04-02T09:00:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-04-02T09:00:00.000Z",
    source_document: null,
  },
  {
    id: 8006,
    estate_id: DEMO_ESTATE_ID_3,
    estate_owner_id: DEMO_OWNER_USER_ID_3,
    user_id: DEMO_OWNER_USER_ID_3,
    first_name: "Raymond",
    last_name: "Delgado",
    middle_name: null,
    nickname: null,
    email: null,
    phone: null,
    address: null,
    relationship: "Ex-spouse",
    legal_role: null,
    profile_picture: null,
    invited: false,
    is_hidden: false,
    source_document_id: null,
    extracted_from_document: false,
    match_key: null,
    last_reviewed_at: null,
    last_reviewed_by: null,
    created_at: "2026-04-02T10:00:00.000Z",
    additional_details: null,
    person_status: null,
    professional_role: null,
    company_or_firm: null,
    sign_off_status: null,
    sign_off_reason: null,
    sign_off_updated_at: null,
    sign_off_updated_by: null,
    death_year: null,
    updated_at: "2026-04-02T10:00:00.000Z",
    source_document: null,
  },
];

export const DEMO_MOCK_INVITATIONS_3: EstateInvitationRow[] = [
  {
    id: 3201,
    estate_id: DEMO_ESTATE_ID_3,
    email: "monica.dreyes@example.com",
    role: "administrator",
    status: "accepted",
    invitation_token: "demo-token-monica",
    invited_by_user_id: DEMO_USER_ID,
    is_attorney: false,
    created_at: "2026-04-01T10:00:00.000Z",
    updated_at: "2026-04-01T11:00:00.000Z",
    expires_at: "2026-05-01T10:00:00.000Z",
  },
  {
    id: 3202,
    estate_id: DEMO_ESTATE_ID_3,
    email: "daniel.delgado@example.com",
    role: "beneficiary",
    status: "pending",
    invitation_token: "demo-token-daniel",
    invited_by_user_id: DEMO_USER_ID,
    is_attorney: false,
    created_at: "2026-04-03T14:00:00.000Z",
    updated_at: "2026-04-03T14:00:00.000Z",
    expires_at: "2026-05-03T14:00:00.000Z",
  },
];

export const DEMO_MOCK_JOINED_USERS_3 = [
  { user_id: DEMO_OWNER_USER_ID_3, email: "monica.dreyes@example.com" },
];

const now3 = "2026-04-03T10:00:00.000Z";

const baseItem3 = (
  fields: Partial<EstateItem> &
    Pick<EstateItem, "id" | "category" | "item_name" | "balance" | "classification">,
): EstateItem => ({
  estate_id: DEMO_ESTATE_ID_3,
  item_type: undefined,
  ownership: null,
  created_at: now3,
  updated_at: now3,
  created_by_user_id: DEMO_OWNER_USER_ID_3,
  last_updated_date: now3,
  notes: undefined,
  plaid_connection_id: null,
  plaid_account_id: null,
  plaid_account_mask: null,
  plaid_last_refreshed_at: null,
  plaid_institution_logo: null,
  source_document_id: null,
  extracted_from_document: false,
  extraction_status: "approved",
  open_date: null,
  identifiers: {},
  metadata: {},
  is_estimated: false,
  balance_estimate_range: null,
  value_label: null,
  status_flag: null,
  linked_item_id: null,
  source_document: null,
  document_item_references: null,
  ...fields,
});

const srcDelgadoFrost = mkDoc(
  6102,
  "Frost_Checking_Statement_Jan2026.pdf",
  "Frost Bank Checking Statement — January 2026",
  "bank_statement",
  "2026-04-02T10:00:00.000Z",
);

const srcDelgadoSchwabSEPIRA = mkDoc(
  6103,
  "Schwab_SEPIRA_Statement_Q42025.pdf",
  "Charles Schwab SEP-IRA Statement — Q4 2025",
  "investment_account_statement",
  "2026-04-02T11:00:00.000Z",
);

const srcDelgadoVanguard = mkDoc(
  6104,
  "Vanguard_Brokerage_Statement_Q42025.pdf",
  "Vanguard Individual Brokerage Statement — Q4 2025",
  "investment_account_statement",
  "2026-04-02T12:00:00.000Z",
);

const srcDelgadoAmplify = mkDoc(
  6105,
  "Amplify_Mortgage_Statement_Jan2026.pdf",
  "Amplify Credit Union Mortgage Statement — January 2026",
  "mortgage_statement",
  "2026-04-03T10:00:00.000Z",
);

const mockAssets3: EstateItem[] = [
  baseItem3({
    id: 5201,
    category: "Properties",
    item_name: "Austin Residence \u2014 2604 Larkspur Valley Drive",
    item_type: "Residential",
    balance: 780_000,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: { address: "2604 Larkspur Valley Drive, Austin, TX 78704" },
    linked_item_id: 5301,
  }),
  baseItem3({
    id: 5202,
    category: "LLC or partnership interest",
    item_name: "Delgado Grounds Management LLC (100%)",
    item_type: "LLC or partnership interest",
    balance: 850_000,
    classification: "asset",
    is_estimated: true,
    value_label: "Pending valuation",
  }),
  baseItem3({
    id: 5203,
    category: "Bank accounts",
    item_name: "Frost Bank Checking (\u20137205)",
    item_type: "Checking accounts",
    balance: 22_415.87,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: { institution: "Frost Bank", account_type: "checking", account_last4: "7205" },
    source_document_id: 6102,
    extracted_from_document: true,
    source_document: srcDelgadoFrost,
  }),
  baseItem3({
    id: 5204,
    category: "Bank accounts",
    item_name: "UFCU Savings (\u20133348)",
    item_type: "Savings accounts",
    balance: 58_900.44,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: { institution: "UFCU", account_type: "savings", account_last4: "3348" },
  }),
  baseItem3({
    id: 5205,
    category: "Retirement accounts",
    item_name: "Charles Schwab SEP-IRA",
    item_type: "IRA (Traditional / Roth / SEP / SIMPLE)",
    balance: 412_300,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: { institution: "Charles Schwab", account_type: "sep_ira" },
    source_document_id: 6103,
    extracted_from_document: true,
    source_document: srcDelgadoSchwabSEPIRA,
    status_flag: ["beneficiary_issue"],
  }),
  baseItem3({
    id: 5206,
    category: "Brokerage accounts",
    item_name: "Vanguard Individual Brokerage",
    item_type: "Individual brokerage account",
    balance: 96_200,
    classification: "asset",
    ownership: "pod_tod",
    identifiers: { institution: "Vanguard", account_type: "brokerage" },
    source_document_id: 6104,
    extracted_from_document: true,
    source_document: srcDelgadoVanguard,
  }),
  baseItem3({
    id: 5207,
    category: "Crypto accounts",
    item_name: "Bitcoin on Coinbase (0.42 BTC)",
    item_type: "Crypto accounts",
    balance: 39_600,
    classification: "asset",
    identifiers: { institution: "Coinbase", asset: "BTC", amount: "0.42" },
  }),
  baseItem3({
    id: 5208,
    category: "Crypto accounts",
    item_name: "Ethereum on Coinbase (3.1 ETH)",
    item_type: "Crypto accounts",
    balance: 8_250,
    classification: "asset",
    identifiers: { institution: "Coinbase", asset: "ETH", amount: "3.1" },
  }),
  baseItem3({
    id: 5209,
    category: "Life insurance",
    item_name: "Principal Life Term (PL-44-9083-2)",
    item_type: "Term life insurance",
    balance: 150_000,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: {
      institution: "Principal Life",
      provider: "Principal Life Insurance Company",
      policy_type: "term",
      policy_number: "PL-44-9083-2",
    },
  }),
  baseItem3({
    id: 5210,
    category: "Vehicles",
    item_name: "2023 Ford F-150",
    item_type: "Cars and motorcycles",
    balance: 42_000,
    classification: "asset",
    ownership: "sole_ownership",
    identifiers: { make: "Ford", model: "F-150", year: "2023" },
  }),
  baseItem3({
    id: 5211,
    category: "Valuables",
    item_name: "Household Furnishings",
    item_type: "Antiques & Furniture",
    balance: 28_000,
    classification: "asset",
    is_estimated: true,
  }),
  baseItem3({
    id: 5212,
    category: "Valuables",
    item_name: "Folk Art Collection (Oaxaca)",
    item_type: "Art & Paintings",
    balance: 15_500,
    classification: "asset",
    is_estimated: true,
  }),
  baseItem3({
    id: 5213,
    category: "Valuables",
    item_name: "Other Personal Effects",
    item_type: "Other valuables",
    balance: 5_000,
    classification: "asset",
  }),
];

const mockDebts3: EstateItem[] = [
  baseItem3({
    id: 5301,
    category: "Secured debts",
    item_name: "Amplify Credit Union Mortgage \u2014 2604 Larkspur Valley",
    item_type: "Mortgages",
    balance: 214_608.39,
    classification: "debt",
    identifiers: { institution: "Amplify Credit Union", loan_type: "mortgage" },
    source_document_id: 6105,
    extracted_from_document: true,
    source_document: srcDelgadoAmplify,
    linked_item_id: 5201,
  }),
];

const totalAssets3 = mockAssets3
  .filter((a) => !a.value_label)
  .reduce((sum, a) => sum + a.balance, 0);
const totalDebts3 = mockDebts3.filter((d) => !d.value_label).reduce((sum, d) => sum + d.balance, 0);

const categoryMap3: Record<string, { value: number; accountCount: number }> = {};
mockAssets3
  .filter((a) => !a.value_label)
  .forEach((item) => {
    if (!categoryMap3[item.category]) categoryMap3[item.category] = { value: 0, accountCount: 0 };
    categoryMap3[item.category].value += item.balance;
    categoryMap3[item.category].accountCount += 1;
  });

const mockCategories3 = Object.entries(categoryMap3).map(([category, data]) => ({
  category,
  value: data.value,
  accountCount: data.accountCount,
  percentage: totalAssets3 > 0 ? (data.value / totalAssets3) * 100 : 0,
  icon: "",
  ...(CATEGORY_COLORS[category] ?? {
    colorClass: "bg-gray-400",
    bgOpacityClass: "bg-gray-400/10",
    textColorClass: "text-gray-400",
  }),
}));

export const DEMO_MOCK_ESTATE_ITEMS_3: EstateItemsResponse = {
  assets: mockAssets3,
  debts: mockDebts3,
  summary: {
    totalAssets: totalAssets3,
    totalDebts: totalDebts3,
    netValue: totalAssets3 - totalDebts3,
    hasItems: true,
    categories: mockCategories3,
    pendingValuationCount: 1,
  },
};

export const DEMO_MOCK_ITEM_BENEFICIARIES_3: Record<number, ItemBeneficiary[]> = {
  5205: [mkBeneficiary(9101, 8006, "Raymond Delgado", "Ex-spouse", 100, null, null, false)],
  5209: [
    mkBeneficiary(9102, 8001, "Monica Delgado-Reyes", "Child", 50, null, null, false),
    mkBeneficiary(9103, 8002, "Daniel Delgado", "Child", 50, null, null, false),
  ],
};

export const DEMO_MOCK_ITEM_DOCUMENT_REFS_3: Record<number, DocumentReference[]> = {
  5203: [
    mkDocRef(
      10101,
      6102,
      5203,
      "approved",
      "Frost_Checking_Statement_Jan2026.pdf",
      "Frost Bank Checking Statement — January 2026",
      "bank_statement",
      "2026-04-02T10:00:00.000Z",
      "2026-04-02T10:30:00.000Z",
    ),
  ],
  5205: [
    mkDocRef(
      10102,
      6103,
      5205,
      "approved",
      "Schwab_SEPIRA_Statement_Q42025.pdf",
      "Charles Schwab SEP-IRA Statement — Q4 2025",
      "investment_account_statement",
      "2026-04-02T11:00:00.000Z",
      "2026-04-02T11:30:00.000Z",
    ),
  ],
  5206: [
    mkDocRef(
      10103,
      6104,
      5206,
      "approved",
      "Vanguard_Brokerage_Statement_Q42025.pdf",
      "Vanguard Individual Brokerage Statement — Q4 2025",
      "investment_account_statement",
      "2026-04-02T12:00:00.000Z",
      "2026-04-02T12:30:00.000Z",
    ),
  ],
  5301: [
    mkDocRef(
      10104,
      6105,
      5301,
      "approved",
      "Amplify_Mortgage_Statement_Jan2026.pdf",
      "Amplify Credit Union Mortgage Statement — January 2026",
      "mortgage_statement",
      "2026-04-03T10:00:00.000Z",
      "2026-04-03T10:30:00.000Z",
    ),
  ],
};

const logEntry3 = (
  id: number,
  created_at: string,
  action_type: ActionType,
  actor_type: ActorType,
  actor_name: string,
  actor_email: string,
  entity_type: string | null = null,
  metadata: Record<string, unknown> = {},
): ActivityLogEntry => ({
  id,
  created_at,
  estate_id: DEMO_ESTATE_ID_3,
  action_type,
  actor_type,
  user_id: actor_type === "lawyer" ? DEMO_USER_ID : DEMO_OWNER_USER_ID_3,
  admin_id: null,
  entity_type,
  entity_id: null,
  metadata,
  actor_name,
  actor_email,
});

const mockActivityEntries3: ActivityLogEntry[] = [
  logEntry3(
    11207,
    "2026-04-03T10:00:00.000Z",
    "uploaded_document",
    "user",
    "Monica Delgado-Reyes",
    "monica.dreyes@example.com",
    "document",
    { document_type: "mortgage_statement", file_name: "Amplify_Mortgage_Statement_Jan2026.pdf" },
  ),
  logEntry3(
    11206,
    "2026-04-02T12:00:00.000Z",
    "uploaded_document",
    "user",
    "Monica Delgado-Reyes",
    "monica.dreyes@example.com",
    "document",
    {
      document_type: "investment_account_statement",
      file_name: "Vanguard_Brokerage_Statement_Q42025.pdf",
    },
  ),
  logEntry3(
    11205,
    "2026-04-02T11:00:00.000Z",
    "uploaded_document",
    "user",
    "Monica Delgado-Reyes",
    "monica.dreyes@example.com",
    "document",
    {
      document_type: "investment_account_statement",
      file_name: "Schwab_SEPIRA_Statement_Q42025.pdf",
    },
  ),
  logEntry3(
    11204,
    "2026-04-02T10:00:00.000Z",
    "uploaded_document",
    "user",
    "Monica Delgado-Reyes",
    "monica.dreyes@example.com",
    "document",
    { document_type: "bank_statement", file_name: "Frost_Checking_Statement_Jan2026.pdf" },
  ),
  logEntry3(
    11203,
    "2026-04-01T12:00:00.000Z",
    "added_estate_item",
    "user",
    "Monica Delgado-Reyes",
    "monica.dreyes@example.com",
    "estate_item",
    { item_name: "Austin Residence", category: "Properties", balance: 780000 },
  ),
  logEntry3(
    11202,
    "2026-04-01T11:00:00.000Z",
    "added_estate_item",
    "user",
    "Monica Delgado-Reyes",
    "monica.dreyes@example.com",
    "estate_item",
    {
      item_name: "Delgado Grounds Management LLC",
      category: "LLC or partnership interest",
      balance: 850000,
    },
  ),
  logEntry3(
    11201,
    "2026-01-22T20:00:00.000Z",
    "uploaded_document",
    "user",
    "Monica Delgado-Reyes",
    "monica.dreyes@example.com",
    "document",
    { document_type: "death_certificate", file_name: "Death_Certificate_Sylvia_Delgado.pdf" },
  ),
];

export const DEMO_MOCK_ACTIVITY_LOG_3: InfiniteData<ActivityLogPage> = {
  pages: [{ entries: mockActivityEntries3, nextCursor: null }],
  pageParams: [null],
};

const itemLogEntry3 = (
  id: number,
  created_at: string,
  action_type: ActionType,
  actor_type: ActorType,
  actor_name: string,
  actor_email: string,
  itemId: number,
  metadata: Record<string, unknown> = {},
): ActivityLogEntry => ({
  id,
  created_at,
  estate_id: DEMO_ESTATE_ID_3,
  action_type,
  actor_type,
  user_id: actor_type === "lawyer" ? DEMO_USER_ID : DEMO_OWNER_USER_ID_3,
  admin_id: null,
  entity_type: "estate_item",
  entity_id: String(itemId),
  metadata,
  actor_name,
  actor_email,
});

export const DEMO_MOCK_ITEM_ACTIVITY_LOGS_3: Record<number, InfiniteData<ActivityLogPage>> = {
  5201: mkItemLog([
    itemLogEntry3(
      11301,
      "2026-04-01T12:00:00.000Z",
      "added_estate_item",
      "user",
      "Monica Delgado-Reyes",
      "monica.dreyes@example.com",
      5201,
    ),
  ]),
  5205: mkItemLog([
    itemLogEntry3(
      11303,
      "2026-04-02T11:30:00.000Z",
      "approved_estate_item",
      "user",
      "Monica Delgado-Reyes",
      "monica.dreyes@example.com",
      5205,
    ),
    itemLogEntry3(
      11302,
      "2026-04-02T11:00:00.000Z",
      "added_estate_item",
      "user",
      "Monica Delgado-Reyes",
      "monica.dreyes@example.com",
      5205,
    ),
  ]),
  5301: mkItemLog([
    itemLogEntry3(
      11305,
      "2026-04-03T10:30:00.000Z",
      "approved_estate_item",
      "user",
      "Monica Delgado-Reyes",
      "monica.dreyes@example.com",
      5301,
    ),
    itemLogEntry3(
      11304,
      "2026-04-03T10:00:00.000Z",
      "added_estate_item",
      "user",
      "Monica Delgado-Reyes",
      "monica.dreyes@example.com",
      5301,
    ),
  ]),
};

export const DEMO_MOCK_OWNER_RELATIONSHIP_3 = "Child";

export const DEMO_MOCK_LAST_ACTIVITY_3 = "2026-04-03T10:00:00.000Z";
